/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getDigitsFor } from '../utils/symbolDigitsStore';
import { createChart, ColorType, LineStyle, LineType, CandlestickSeries, LineSeries, HistogramSeries, createSeriesMarkers, AreaSeries, BaselineSeries } from 'lightweight-charts';
import { usePriceFeed } from '../context/PriceFeedContext';
import { canonicalizeIndicatorData, getIndicatorKey, padIndicatorData } from '../utils/indicatorUtils';
import IndicatorControls from './IndicatorControls';
import { Button } from './ui/button';
import { Modal } from './ui/modal';
import CustomSelect from './ui/CustomSelect';
import {
    listChartProfiles,
    createChartProfile,
    deleteChartProfile,
    getAccountTrades,
    getAgentExecution,
    launchStudioChartAnalysis,
} from '../api/api';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { useDateTimeFormatting } from '../utils/datetime';
import { DrawingToolsManager, TOOL_TYPES } from '../utils/drawingToolsManager';
import { buildPositionRiskLevels } from '../utils/tradingChartUtils';

const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
};

const detachMarkerPrimitive = (primitive) => {
    try { primitive?.detach?.(); } catch { /* ignore cleanup errors */ }
};

// Converts OHLCV data (d.x: ms, d.y: [open, high, low, close])
function convertOhlcvToBars(ohlcvData) {
    if (!Array.isArray(ohlcvData)) return [];
    return ohlcvData
        .filter(d =>
            d &&
            typeof d.time === "number" &&
            typeof d.open === "number" &&
            typeof d.high === "number" &&
            typeof d.low === "number" &&
            typeof d.close === "number"
        )
        .map(d => {
            let timeInSeconds = d.time;
            // If time is in milliseconds, convert to seconds
            if (timeInSeconds > 1000000000000) {
                timeInSeconds = Math.floor(timeInSeconds / 1000);
            }
            return {
                time: timeInSeconds,
                open: d.open,
                high: d.high,
                low: d.low,
                close: d.close,
            };
        });
}

// Converts trades to chart markers
function convertMarkers(tradeMarkers) {
    if (!Array.isArray(tradeMarkers)) return [];
    const flatMarkers = tradeMarkers.flatMap(t => {
        let entryTimeInSeconds = t.entry_timestamp;
        if (entryTimeInSeconds > 1000000000000) {
            entryTimeInSeconds = Math.floor(entryTimeInSeconds / 1000);
        }
        const entry = {
            time: entryTimeInSeconds,
            position: "belowBar",
            color: t.direction === "BUY" ? "#26a69a" : "#ef5350",
            shape: "arrowUp",
            text: `${t.direction}`,
        };
        let exit = null;
        if (t.exit_timestamp && t.exit_price !== null) {
            let exitTimeInSeconds = t.exit_timestamp;
            if (exitTimeInSeconds > 1000000000000) {
                exitTimeInSeconds = Math.floor(exitTimeInSeconds / 1000);
            }
            exit = {
                time: exitTimeInSeconds,
                position: "aboveBar",
                color: t.pnl >= 0 ? "#26a69a" : "#ef5350",
                shape: "arrowDown",
                text: `Exit: ${t.pnl.toFixed(0)}`,
            };
        }
        return exit ? [entry, exit] : [entry];
    });

    // Remove any invalid or duplicate times and sort
    const cleaned = flatMarkers
        .filter(m => typeof m.time === "number" && !isNaN(m.time))
        .sort((a, b) => a.time - b.time);

    // Optionally: remove duplicates (if same time)
    const deduped = [];
    let lastTime = -Infinity;
    for (const m of cleaned) {
        if (m.time > lastTime) {
            deduped.push(m);
            lastTime = m.time;
        }
    }
    return deduped;
}

// Build markers for recent account trades (live)
function buildRecentTradeMarkers(trades, symbol) {
    if (!Array.isArray(trades)) return [];
    const filtered = trades
        .filter(t => !symbol || String(t.instrument).toUpperCase() === String(symbol).toUpperCase())
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 3);

    const markers = [];
    for (const t of filtered) {
        const dir = String(t.direction || '').toUpperCase();
        const pnlNum = t.actual_profit_loss != null ? Number(t.actual_profit_loss) : null;
        const entryTs = t.created_at ? Math.floor(new Date(t.created_at).getTime() / 1000) : null;
        const exitTs = t.closed_at ? Math.floor(new Date(t.closed_at).getTime() / 1000) : null;

        if (Number.isFinite(entryTs)) {
            markers.push({
                time: entryTs,
                position: 'belowBar',
                color: dir === 'BUY' ? '#26a69a' : '#ef5350',
                shape: 'arrowUp',
                text: `${dir}`,
            });
        }
        if (Number.isFinite(exitTs) && pnlNum != null && Number.isFinite(pnlNum)) {
            markers.push({
                time: exitTs,
                position: 'aboveBar',
                color: pnlNum >= 0 ? '#26a69a' : '#ef5350',
                shape: 'arrowDown',
                text: `Exit: ${pnlNum.toFixed(2)}`,
            });
        }
    }
    return markers.sort((a, b) => a.time - b.time);
}

// De-duplicate data by time, keeping the first entry
function deduplicateData(data) {
    if (!data || data.length === 0) return [];
    const deduped = [data[0]];
    for (let i = 1; i < data.length; i++) {
        if (data[i].time > deduped[deduped.length - 1].time) {
            deduped.push(data[i]);
        }
    }
    return deduped;
}

const indicatorColors = [
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
    '#FFA500', // Orange
    '#800080', // Purple
    '#008000', // Dark Green
    '#800000', // Maroon
];

// Default color for negative values when using baseline coloring on pane indicators
const NEGATIVE_LINE_COLOR = '#ff4d4f';

// ---- Visual schema helpers ----
const lineTypeMap = {
    simple: LineType?.Simple ?? 0,
    withsteps: LineType?.WithSteps ?? 1,
    curved: LineType?.Curved ?? 2,
};

const lineStyleMap = {
    solid: LineStyle.Solid ?? 0,
    dotted: LineStyle.Dotted ?? 1,
    dashed: LineStyle.Dashed ?? 2,
};

function deepMerge(target, source) {
    if (!source) return target ? JSON.parse(JSON.stringify(target)) : {};
    if (!target) return JSON.parse(JSON.stringify(source));
    const output = Array.isArray(target) ? [...target] : { ...target };
    Object.keys(source).forEach((key) => {
        const sVal = source[key];
        const tVal = output[key];
        if (sVal && typeof sVal === 'object' && !Array.isArray(sVal)) {
            output[key] = deepMerge(tVal && typeof tVal === 'object' ? tVal : {}, sVal);
        } else {
            output[key] = sVal;
        }
    });
    return output;
}

function resolveSeriesType(plotType) {
    switch ((plotType || 'line').toLowerCase()) {
        case 'area':
            return { SeriesCtor: AreaSeries, type: 'area' };
        case 'histogram':
            return { SeriesCtor: HistogramSeries, type: 'histogram' };
        case 'baseline':
            return { SeriesCtor: BaselineSeries, type: 'baseline' };
        case 'line':
        default:
            return { SeriesCtor: LineSeries, type: 'line' };
    }
}

function buildOptionsFromVisuals(visualForKey, fallbacks = {}) {
    const opts = {};
    if (visualForKey) {
        if (typeof visualForKey.color === 'string') opts.color = visualForKey.color;
        if (typeof visualForKey.lineWidth === 'number') opts.lineWidth = visualForKey.lineWidth;
        if (typeof visualForKey.lineStyle === 'string') {
            opts.lineStyle = lineStyleMap[visualForKey.lineStyle] ?? undefined;
        }
        if (typeof visualForKey.lineType === 'string') {
            opts.lineType = lineTypeMap[visualForKey.lineType.toLowerCase()] ?? undefined;
        }
        if (typeof visualForKey.positiveColor === 'string') opts.positiveColor = visualForKey.positiveColor;
        if (typeof visualForKey.negativeColor === 'string') opts.negativeColor = visualForKey.negativeColor;
        if (typeof visualForKey.visible === 'boolean') opts.visible = visualForKey.visible;
    }
    // apply fallbacks when not provided by visuals
    if (opts.color === undefined && fallbacks.color) opts.color = fallbacks.color;
    if (opts.lineWidth === undefined && fallbacks.lineWidth) opts.lineWidth = fallbacks.lineWidth;
    if (opts.lineStyle === undefined && fallbacks.lineStyle !== undefined) opts.lineStyle = fallbacks.lineStyle;
    return opts;
}

// Convert generic options to series-type-specific options
function toSeriesTypeOptions(seriesType, opts) {
    const type = seriesType; // 'line' | 'area' | 'histogram'
    if (type === 'line') {
        const lineOpts = {
            color: opts.color,
            lineWidth: opts.lineWidth,
            lineStyle: opts.lineStyle,
            visible: opts.visible,
        };
        if (opts.lineType !== undefined) lineOpts.lineType = opts.lineType;
        return lineOpts;
    }
    if (type === 'baseline') {
        // Show only colored line above/below base by making fills transparent
        const neg = opts.negativeColor || NEGATIVE_LINE_COLOR;
        return {
            baseValue: { type: 'price', price: 0 },
            topLineColor: opts.color || '#FFD700',
            bottomLineColor: neg,
            topFillColor1: 'rgba(0,0,0,0)',
            topFillColor2: 'rgba(0,0,0,0)',
            bottomFillColor1: 'rgba(0,0,0,0)',
            bottomFillColor2: 'rgba(0,0,0,0)',
            lineWidth: opts.lineWidth,
            visible: opts.visible,
        };
    }
    if (type === 'histogram') {
        return {
            color: opts.positiveColor || opts.color,
            // histogram respects color; width/style not applicable
            visible: opts.visible,
        };
    }
    // area
    const color = opts.color || '#FFD700';
    // Derive semi-transparent fills from color; simple fallback if not hex
    const hex = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(color);
    const withAlpha = (hexColor, alpha) => {
        if (!/^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(hexColor)) return color;
        const c = hexColor.replace('#','');
        const expand = c.length === 3 ? c.split('').map(ch => ch+ch).join('') : c;
        const r = parseInt(expand.slice(0,2),16);
        const g = parseInt(expand.slice(2,4),16);
        const b = parseInt(expand.slice(4,6),16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    return {
        lineColor: color,
        topColor: hex ? withAlpha(color, 0.2) : color,
        bottomColor: hex ? withAlpha(color, 0.02) : color,
        lineWidth: opts.lineWidth,
        visible: opts.visible,
    };
}

function setIndicatorSeriesData(series, points, context = 'indicator', seriesType) {
    const safePoints = canonicalizeIndicatorData(points);
    const renderPoints = seriesType === 'baseline' && !safePoints.some((point) => Number.isFinite(point?.value))
        ? []
        : safePoints;
    try {
        series?.setData(renderPoints);
    } catch (error) {
        console.error(`Failed to render ${context} data; clearing the affected series`, error);
        try { series?.setData([]); } catch { /* ignore recovery errors */ }
    }
}

function applySeriesPointVisuals(points, seriesType, options = {}) {
    const safePoints = canonicalizeIndicatorData(points);
    if (seriesType !== 'histogram') return safePoints;
    const positiveColor = options.positiveColor || options.color;
    const negativeColor = options.negativeColor || positiveColor;
    if (!positiveColor && !negativeColor) return safePoints;
    return safePoints.map((point) => {
        if (!Number.isFinite(point?.value)) return point;
        const color = point.value < 0 ? negativeColor : positiveColor;
        return color ? { ...point, color } : point;
    });
}

// Determine a sensible precision for a series based on its smallest non-zero value.
// This ensures very small indicator values (e.g. ATR on forex) are not shown as 0.00.
function computePrecisionForPoints(points, defaultPrecision = 2, maxPrecision = 8) {
    if (!Array.isArray(points) || points.length === 0) return defaultPrecision;
    let minNonZero = Infinity;
    for (const p of points) {
        const v = p && (p.value !== undefined ? p.value : (p.y !== undefined ? p.y : undefined));
        if (typeof v !== 'number' || !Number.isFinite(v)) continue;
        const a = Math.abs(v);
        if (a > 0 && a < minNonZero) minNonZero = a;
    }
    if (!Number.isFinite(minNonZero) || minNonZero === Infinity) return defaultPrecision;
    if (minNonZero >= 1) return Math.max(defaultPrecision, 2);
    const precision = Math.ceil(-Math.log10(minNonZero));
    return Math.min(Math.max(defaultPrecision, precision), maxPrecision);
}

function derivePriceFormatFromPoints(points, cachedDigits) {
    const defaultPrecision = Number.isFinite(cachedDigits) ? Number(cachedDigits) : 2;
    const precision = computePrecisionForPoints(points, defaultPrecision, 8);
    const pf = { type: 'price', precision };
    // Derive a sensible minMove when digits are known
    if (Number.isFinite(precision) && precision >= 0 && precision <= 12) {
        pf.minMove = Math.pow(10, -precision);
    }
    return pf;
}

function normalizeIndicatorMetaKey(value) {
    return String(value || '').trim().toLowerCase();
}

function indicatorMetaMatches(meta, candidate) {
    if (!meta || typeof meta !== 'object' || !candidate) return false;
    const normalized = normalizeIndicatorMetaKey(candidate);
    return [meta.name, meta.display_name, meta.id]
        .some((value) => normalizeIndicatorMetaKey(value) === normalized);
}

function isSupportResistanceIndicator(indicator) {
    const name = normalizeIndicatorMetaKey(indicator?.name).replace(/[_\s-]/g, '');
    return name === 'supportresistance';
}

// Resolve indicator metadata from the exact metadata captured during websocket
// hydration first, then tolerate every API collection shape and name casing.
function getIndicatorMeta(availableIndicators, indicatorOrName) {
    const indicator = (
        indicatorOrName && typeof indicatorOrName === 'object'
            ? indicatorOrName
            : null
    );
    const embeddedMeta = indicator?.indicatorMeta;
    if (embeddedMeta && typeof embeddedMeta === 'object') return embeddedMeta;

    const name = indicator
        ? (indicator.name || indicator.display_name)
        : indicatorOrName;
    const normalizedName = normalizeIndicatorMetaKey(name);
    if (!availableIndicators || !normalizedName) return null;

    const collections = [];
    if (Array.isArray(availableIndicators)) {
        collections.push(availableIndicators.map((meta) => [null, meta]));
    } else if (typeof availableIndicators === 'object') {
        collections.push(Object.entries(availableIndicators));
        if (
            availableIndicators.indicators
            && typeof availableIndicators.indicators === 'object'
        ) {
            collections.push(Object.entries(availableIndicators.indicators));
        }
    }

    for (const entries of collections) {
        for (const [key, meta] of entries) {
            if (
                normalizeIndicatorMetaKey(key) === normalizedName
                || indicatorMetaMatches(meta, name)
            ) {
                return meta;
            }
        }
    }
    return null;
}

function getBacktestIndicatorMeta(availableIndicators, indicatorKey, group) {
    const embedded = group?.meta && typeof group.meta === 'object' ? group.meta : {};
    const embeddedName = embedded.indicator_name || embedded.name || embedded.display_name;
    let registryMeta = getIndicatorMeta(availableIndicators, embeddedName);

    if (!registryMeta && availableIndicators && indicatorKey) {
        const collections = [];
        if (Array.isArray(availableIndicators)) {
            collections.push(availableIndicators.map((meta) => [null, meta]));
        } else if (typeof availableIndicators === 'object') {
            collections.push(Object.entries(availableIndicators));
            if (availableIndicators.indicators && typeof availableIndicators.indicators === 'object') {
                collections.push(Object.entries(availableIndicators.indicators));
            }
        }
        const normalizedGroupKey = normalizeIndicatorMetaKey(indicatorKey);
        const candidates = [];
        collections.flat().forEach(([key, meta]) => {
            if (!meta || typeof meta !== 'object') return;
            [key, meta.name, meta.display_name, meta.id].forEach((candidate) => {
                const normalized = normalizeIndicatorMetaKey(candidate);
                if (
                    normalized
                    && (
                        normalizedGroupKey === normalized
                        || normalizedGroupKey.startsWith(`${normalized}_`)
                    )
                ) {
                    candidates.push({ normalized, meta });
                }
            });
        });
        candidates.sort((a, b) => b.normalized.length - a.normalized.length);
        registryMeta = candidates[0]?.meta || null;
    }

    const outputs = (
        Array.isArray(embedded.outputs) && embedded.outputs.length > 0
            ? embedded.outputs
            : (
                Array.isArray(registryMeta?.outputs) && registryMeta.outputs.length > 0
                    ? registryMeta.outputs
                    : Object.keys(group?.outputs || {})
            )
    );
    return {
        ...(registryMeta || {}),
        ...embedded,
        name: embedded.indicator_name || registryMeta?.name || indicatorKey,
        outputs,
        params: embedded.params || group?.params || {},
        visual_schema: embedded.visual_schema || group?.visual_schema || registryMeta?.visual_schema || {},
        visual_defaults: embedded.visual_defaults || group?.visual_defaults || registryMeta?.visual_defaults || {},
    };
}

function normalizeBacktestIndicatorPoints(rawPoints) {
    if (!Array.isArray(rawPoints)) return [];
    const formatted = rawPoints
        .filter((point) => point && Number.isFinite(Number(point.time)))
        .map((point) => {
            let time = Number(point.time);
            if (time > 1000000000000) time = Math.floor(time / 1000);
            const normalized = { time };
            if (Number.isFinite(Number(point.value))) normalized.value = Number(point.value);
            if (typeof point.color === 'string') normalized.color = point.color;
            return normalized;
        })
        .sort((a, b) => a.time - b.time);
    return deduplicateData(formatted);
}

function normalizeBacktestEventTime(value) {
    let seconds = Number(value);
    if (!Number.isFinite(seconds) && typeof value === 'string') {
        const parsed = Date.parse(value);
        if (Number.isFinite(parsed)) seconds = Math.floor(parsed / 1000);
    }
    if (!Number.isFinite(seconds)) return null;
    return seconds > 1000000000000 ? Math.floor(seconds / 1000) : seconds;
}

function backtestMarkerPosition(visualKey, rawShape, direction) {
    const key = String(visualKey || '').toLowerCase();
    const shape = String(rawShape || '').toLowerCase();
    if (
        direction === 'bearish'
        || /bear|high|shooting|gravestone|hanging/.test(key)
        || /down|inverted/.test(shape)
    ) return 'aboveBar';
    return 'belowBar';
}

function backtestMarkerShape(rawShape, position) {
    const shape = String(rawShape || '').trim().toLowerCase();
    if (shape === 'dot' || shape === 'circle') return 'circle';
    if (shape === 'square') return 'square';
    if (shape === 'arrow_up' || shape === 'arrowup') return 'arrowUp';
    if (shape === 'arrow_down' || shape === 'arrowdown') return 'arrowDown';
    if (shape === 'inverted_triangle') return 'arrowDown';
    if (shape === 'triangle') return position === 'aboveBar' ? 'arrowDown' : 'arrowUp';
    return position === 'aboveBar' ? 'arrowDown' : 'arrowUp';
}

function divergenceEventVisualKey(event) {
    if (event?.direction === 'bullish') return event?.hidden ? 'bull_hidden_div' : 'bull_div';
    return event?.hidden ? 'bear_hidden_div' : 'bear_div';
}

function getDeclaredVisualSeriesKeys(indicatorMeta) {
    const schemaKeys = Object.keys(indicatorMeta?.visual_schema?.series || {});
    const defaultKeys = Object.keys(indicatorMeta?.visual_defaults?.series || {});
    const outputKeys = Array.isArray(indicatorMeta?.outputs) ? indicatorMeta.outputs : [];
    return [...new Set([...schemaKeys, ...defaultKeys, ...outputKeys])];
}

function runtimeSeriesKeyMatches(candidateKey, declaredKey) {
    const candidate = normalizeIndicatorMetaKey(candidateKey);
    const declared = normalizeIndicatorMetaKey(declaredKey);
    return Boolean(
        candidate
        && declared
        && (candidate === declared || candidate.endsWith(`_${declared}`))
    );
}

// Resolve a runtime output key to the registered visual key. Declared keys must
// win over legacy runtime-prefixed override keys, because only declared keys are
// exposed and editable in the indicator settings UI.
function pickSeriesVisual(visualsByKey, indicatorMeta, candidateKey) {
    if (!visualsByKey || typeof visualsByKey !== 'object') return { key: null, visual: undefined };

    const declaredKeys = getDeclaredVisualSeriesKeys(indicatorMeta)
        .filter((key) => visualsByKey[key])
        .sort((a, b) => String(b).length - String(a).length);
    if (candidateKey) {
        const declaredMatch = declaredKeys.find((key) => runtimeSeriesKeyMatches(candidateKey, key));
        if (declaredMatch) {
            return { key: declaredMatch, visual: visualsByKey[declaredMatch] };
        }

        // Metadata-less indicators still need to support direct runtime keys.
        if (visualsByKey[candidateKey]) {
            return { key: candidateKey, visual: visualsByKey[candidateKey] };
        }
        const fallbackMatch = Object.keys(visualsByKey)
            .sort((a, b) => String(b).length - String(a).length)
            .find((key) => runtimeSeriesKeyMatches(candidateKey, key));
        if (fallbackMatch) {
            return { key: fallbackMatch, visual: visualsByKey[fallbackMatch] };
        }
    }

    if (visualsByKey.default) return { key: 'default', visual: visualsByKey.default };

    const keys = Object.keys(visualsByKey);
    if (keys.length === 1) return { key: keys[0], visual: visualsByKey[keys[0]] };
    return { key: null, visual: undefined };
}

function isMarkerVisualOutput(visualsByKey, indicatorMeta, candidateKey) {
    const visual = pickSeriesVisual(visualsByKey, indicatorMeta, candidateKey).visual;
    return ['marker', 'markers'].includes(
        String(visual?.plotType || '').toLowerCase(),
    );
}

// Heuristic: detect very small-scale outputs (e.g., EMA slope) that should live in their own pane
function isSlopeOutputKey(key) {
    const k = String(key || '').toLowerCase();
    return k.endsWith('_slope') || k.endsWith('_slope_pct') || /(^|_)slope(_|$)/.test(k);
}

// Distinguish percentage-form slope keys
function isSlopePercentKey(key) {
    const k = String(key || '').toLowerCase();
    return k.endsWith('_slope_pct') || k.endsWith('_slope_pc') || /(^|_)slope_pct($|_)/.test(k) || /(^|_)slope_pc($|_)/.test(k);
}

// Determine which slope output user prefers based on params or visuals
function chooseAllowedSlopeKeys(allKeys, params = {}, visualsByKey = {}) {
    const slopeKeys = (allKeys || []).filter(isSlopeOutputKey);
    if (slopeKeys.length <= 1) return new Set(slopeKeys);
    // 1) Check visuals: if exactly one slope key is visible, pick it
    try {
        const visibleSlope = slopeKeys.filter(k => (visualsByKey?.[k]?.visible !== false));
        const hiddenSlope = slopeKeys.filter(k => (visualsByKey?.[k]?.visible === false));
        if (visibleSlope.length === 1) return new Set([visibleSlope[0]]);
        if (visibleSlope.length === 0 && hiddenSlope.length === slopeKeys.length - 1) {
            // All but one are hidden; pick the remaining visible (none) → default fallback below
        }
    } catch { /* ignore */ }
    // 2) Check params: slope_output / slope_type / slope_mode string
    const prefStr = String(params?.slope_output || params?.slope_type || params?.slope_mode || '').toLowerCase();
    if (prefStr) {
        if (/(percent|percentage|pct|pc)/.test(prefStr)) {
            const key = slopeKeys.find(isSlopePercentKey) || slopeKeys[0];
            return new Set([key]);
        }
        if (/(delta|value|raw)/.test(prefStr)) {
            const key = slopeKeys.find(k => isSlopeOutputKey(k) && !isSlopePercentKey(k)) || slopeKeys[0];
            return new Set([key]);
        }
    }
    // 3) Check boolean flags: slope_pct / slope_pc / percent vs slope / delta
    const pctLike = !!(params?.slope_pct || params?.slope_pc || params?.percent || params?.percentage);
    const deltaLike = !!(params?.slope || params?.delta || params?.value || params?.raw);
    if (pctLike && !deltaLike) {
        const key = slopeKeys.find(isSlopePercentKey) || slopeKeys[0];
        return new Set([key]);
    }
    if (deltaLike && !pctLike) {
        const key = slopeKeys.find(k => isSlopeOutputKey(k) && !isSlopePercentKey(k)) || slopeKeys[0];
        return new Set([key]);
    }
    // 4) Default: prefer percent if present, else delta
    const prefer = slopeKeys.find(isSlopePercentKey) || slopeKeys[0];
    return new Set([prefer]);
}

// Persisted cache key for per-indicator visual overrides
const OVERRIDES_STORAGE_KEY = 'indicatorVisualOverrides:v1';

// Stable stringify for params to build a signature
function stableStringify(obj) {
    const seen = new WeakSet();
    const helper = (value) => {
        if (value === null || typeof value !== 'object') return JSON.stringify(value);
        if (Array.isArray(value)) return '[' + value.map(helper).join(',') + ']';
        if (seen.has(value)) return '"[Circular]"';
        seen.add(value);
        const keys = Object.keys(value).sort();
        return '{' + keys.map(k => JSON.stringify(k) + ':' + helper(value[k])).join(',') + '}';
    };
    return helper(obj || {});
}

function hasVisualOverrideValues(value) {
    if (value == null) return false;
    if (Array.isArray(value)) return value.some(hasVisualOverrideValues);
    if (typeof value !== 'object') return true;
    return Object.values(value).some(hasVisualOverrideValues);
}

function getIndicatorSignature(indicator) {
    if (!indicator) return '';
    const name = indicator.name || '';
    const pane = indicator.paneType || '';
    const paramsStr = stableStringify(indicator.params || {});
    return `${name}|${pane}|${paramsStr}`;
}

const TradingChart = (props) => {
     const {
         selectedAccount,
         selectedSymbol,
         selectedTimeframe,
         stopLossPrice,
         takeProfitPrice,
         openPositions = [],
         tickSize,
         // Optional: pip size for better precision on some brokers (e.g., cTrader)
         pipSize,
         // Optional: explicit digits from backend when available
         digits,
         // New props for backtesting
         ohlcvData,
         tradeMarkers,
         extraMarkers = [],
         backtestIndicatorData,
         availableIndicators: availableIndicatorsProp,
         isBacktest = false,
         height, // If provided, use fixed height; otherwise fill parent
         selectedTrade,
        // New optional props for timeframe overlay
         showTimeframeOverlay = false,
         timeframeOptions,
         onTimeframeChange,
         // New: compact controls
         controlsCompact = false,
         // New: chart integration - notify parent when a bar is clicked
         onBarSelected,
        // New: show last 3 account trades overlay (live only)
         showRecentTrades = false,
         onToggleRecentTrades,
         // New: force-show the controls bar even in backtest mode
         showControls = false,
         // Optional: load-more callback for backtest/DW charts
         loadMoreHistoryProp,
         // Studio-only bounded chart analysis affordance
         enableStudioChartAnalysis = false,
     } = props;

    const chartContainerRef = useRef(null);
    const paneOverlayHostRef = useRef(null);
    const paneMeasureTimerRef = useRef(null);
    const paneTopPropertyCountRef = useRef(0);
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);
    // Dedicated volume pane index (assigned lazily when first needed)
    const volumePaneIndexRef = useRef(null);
    // Backtest volume overlay (MA on volume) series refs
    const volumeOverlaySeriesRef = useRef(new Map()); // key -> series
    const [backtestVolumeOverlayLabels, setBacktestVolumeOverlayLabels] = useState([]); // [{id,text,color}]
    const markersRef = useRef(null);
    const recentTradeMarkersRef = useRef([]);
    const indicatorSeriesRef = useRef(new Map());
    const indicatorSparseMarkersRef = useRef(new Map());
    const indicatorEventMarkersRef = useRef(new Map());
    const indicatorEventSegmentsRef = useRef(new Map());
    const indicatorGuideLinesRef = useRef(new Map());
    const specSeriesRef = useRef(new Map());
    const stopLossLineRef = useRef(null);
    const takeProfitLineRef = useRef(null);
    const positionRiskLinesRef = useRef(new Map());
    const supportResistanceLinesRef = useRef(new Map()); // Map<indicatorId, Array<priceLine>>
    const backtestIndicatorLineRefs = useRef(new Map()); // For backtest indicators
    const backtestSeriesKindRef = useRef(new Map()); // seriesId -> 'line' | 'histogram'
    const backtestIndicatorMarkersRef = useRef(new Map());
    const backtestIndicatorSegmentsRef = useRef(new Map());
    const backtestIndicatorGuidesRef = useRef(new Map());
    const visualOverridesCacheRef = useRef(new Map()); // signature -> overrides
    const clearMarkerPrimitives = useCallback(() => {
        detachMarkerPrimitive(markersRef.current);
        markersRef.current = null;
        recentTradeMarkersRef.current = [];

        [indicatorSparseMarkersRef, indicatorEventMarkersRef, backtestIndicatorMarkersRef].forEach((ref) => {
            ref.current.forEach((entry) => detachMarkerPrimitive(entry?.primitive || entry));
            ref.current.clear();
        });
    }, []);

    // New: keep a reference of current bars and fast lookup time -> index
    const barsRef = useRef([]);
    const timeToIndexRef = useRef(new Map());
    const mainSeriesStateRef = useRef({ contextKey: '', lastTime: null });
    // Scroll-restore: when backfill prepends bars, store the target range here
    // so the historicalData effect can restore viewport AFTER setData.
    const pendingScrollRestoreRef = useRef(null);
    const chartDataContextRef = useRef('');
    chartDataContextRef.current = `${selectedAccount || ''}|${selectedSymbol || ''}|${selectedTimeframe || ''}`;
    const chartResetContextRef = useRef(
        `${chartDataContextRef.current}|${isBacktest ? 'backtest' : 'live'}`,
    );
    const heightRef = useRef(height);
    heightRef.current = height;
    // New: keep latest callback without re-initting chart
    const onBarSelectedRef = useRef(onBarSelected);
    useEffect(() => { onBarSelectedRef.current = onBarSelected; }, [onBarSelected]);

    // ── Drawing tools state ──────────────────────────────────────────────────
    const drawingCanvasRef = useRef(null);
    const drawingManagerRef = useRef(null);
    const [activeDrawingTool, setActiveDrawingTool] = useState(TOOL_TYPES.NONE);
    const [drawingSelection, setDrawingSelection] = useState(false);
    const [drawingCount, setDrawingCount] = useState(0);
    const [drawings, setDrawings] = useState([]);
    const [selectedDrawingId, setSelectedDrawingId] = useState(null);
    const [chartAnalysisModalOpen, setChartAnalysisModalOpen] = useState(false);
    const [chartAnalysisState, setChartAnalysisState] = useState({
        status: 'idle',
        error: '',
        executionId: null,
        sessionId: null,
        preflight: null,
        result: null,
    });
    const analysisPollTimeoutRef = useRef(null);

    // Helper: return to the live edge without changing the user's zoom level.
    const centerOnLastBar = useCallback(() => {
        try {
            const chart = chartRef.current;
            if (!chart || !Array.isArray(barsRef.current) || barsRef.current.length === 0) return false;
            // Don't override when a trade focus is active; that effect will set its own range
            if (props?.selectedTrade) return false;
            chart.timeScale().scrollToRealTime();
            return true;
    } catch { return false; }
    }, [props?.selectedTrade]);

    // Volume overlay toggle (local)
    const [showVolume, setShowVolume] = useState(true);
    useEffect(() => {
        if (!showVolume && chartRef.current && volumeSeriesRef.current) {
            chartRef.current.removeSeries(volumeSeriesRef.current);
            volumeSeriesRef.current = null;
        }
    }, [showVolume]);

    // Always call hooks unconditionally; select stub for backtest
    const livePriceFeed = usePriceFeed();
    const backtestStub = useMemo(() => ({
        candle: null,
        historicalData: [],
        historyStatus: 'ready',
        historyError: null,
        retryHistory: () => {},
        activeIndicators: [],
        availableIndicators: [],
        sendIndicatorAction: () => {},
        setActiveIndicators: () => {},
        removeActiveIndicator: () => false,
        indicatorLiveUpdate: null,
        isLoggingEnabled: false,
        // Spec streaming fallbacks (no-ops for backtest mode)
        specStreams: {},
        subscribeSpec: () => {},
        unsubscribeSpec: () => {},
    }), []);
    const priceFeed = isBacktest ? backtestStub : livePriceFeed;

    useEffect(() => {
        return () => {
            if (analysisPollTimeoutRef.current) {
                clearTimeout(analysisPollTimeoutRef.current);
            }
        };
    }, []);

    // One-time auto-center gating state per context (account|symbol|timeframe)
    const centeredContextKeyRef = useRef('');
    const didCenterRef = useRef(false);

    const { candle, historicalData, historyStatus, historyError, retryHistory, activeIndicators, availableIndicators: feedAvailableIndicators, sendIndicatorAction, setActiveIndicators, removeActiveIndicator, isLoggingEnabled, indicatorLiveUpdate, loadMoreHistory: loadMoreHistoryFeed, specStreams, subscribeSpec, unsubscribeSpec } = priceFeed;
    const availableIndicators = (
        isBacktest && availableIndicatorsProp != null
            ? availableIndicatorsProp
            : feedAvailableIndicators
    );
    const paneLayoutKey = `${selectedAccount || ''}|${selectedSymbol || ''}|${selectedTimeframe || ''}|${showVolume ? 'volume' : 'no-volume'}|${(activeIndicators || [])
        .filter((indicator) => indicator?.paneType === 'pane')
        .map((indicator) => indicator.id)
        .join('|')}`;
    // Use the prop callback if provided (DW/backtest), otherwise use the feed's callback (live)
    const loadMoreHistory = loadMoreHistoryProp || loadMoreHistoryFeed;

    // Map from compressed time → real time so axis/crosshair show correct dates
    const timeRealMapRef = useRef(new Map());
    useEffect(() => {
        const src = isBacktest ? ohlcvData : historicalData;
        if (!Array.isArray(src) || src.length === 0) return;
        const m = new Map();
        for (const bar of src) {
            if (bar.time_real != null && bar.time_real !== bar.time) m.set(bar.time, bar.time_real);
        }
        timeRealMapRef.current = m;
    }, [isBacktest, ohlcvData, historicalData]);

    // Profiles (only for live trading)
    const [chartProfiles, setChartProfiles] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState('');
    const [chartRenderError, setChartRenderError] = useState(null);

    // Lightweight Charts can throw from its canvas animation frame, after setData
    // has returned. Recover the overlays in that case so Studio remains usable.
    useEffect(() => {
        if (isBacktest) return undefined;
        const handleChartRuntimeError = (event) => {
            const message = `${event?.message || ''} ${event?.error?.message || ''}`.toLowerCase();
            if (!/lightweight|canvasgradient|non-finite|value is null|oldest data|invalid time/.test(message)) return;
            console.error('Recovering Studio chart after a Lightweight Charts render error:', event?.error || event?.message);
            indicatorSeriesRef.current.forEach(({ series }) => {
                try { series?.setData([]); } catch { /* ignore recovery errors */ }
            });
            setChartRenderError('An indicator produced invalid chart data and was cleared.');
        };
        window.addEventListener('error', handleChartRuntimeError);
        return () => window.removeEventListener('error', handleChartRuntimeError);
    }, [isBacktest]);

    const normalizeChartProfiles = useCallback((data) => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.results)) return data.results;
        return [];
    }, []);

    useEffect(() => {
        if (!isBacktest) {
            listChartProfiles()
                .then((data) => setChartProfiles(normalizeChartProfiles(data)))
                .catch(console.error);
        }
    }, [isBacktest, normalizeChartProfiles]);

    const normalizeIndicatorPayload = useCallback((indicator) => {
        if (!indicator || !indicator.name) return null;
        return {
            indicator_name: indicator.name,
            params: indicator.params && typeof indicator.params === 'object' ? indicator.params : {},
        };
    }, []);

    const getCurrentVisibleRange = useCallback(() => {
        try {
            const bars = Array.isArray(barsRef.current) ? barsRef.current : [];
            const realTimeForBar = (bar) => {
                const compressedTime = Number(bar?.time);
                const mappedRealTime = timeRealMapRef.current.get(compressedTime);
                return Number(bar?.time_real ?? mappedRealTime ?? compressedTime);
            };
            const logicalRange = chartRef.current?.timeScale()?.getVisibleLogicalRange?.();
            if (
                bars.length > 0
                && logicalRange
                && Number.isFinite(logicalRange.from)
                && Number.isFinite(logicalRange.to)
            ) {
                const firstIndex = Math.max(0, Math.floor(logicalRange.from));
                const lastIndex = Math.min(bars.length - 1, Math.ceil(logicalRange.to));
                if (firstIndex <= lastIndex) {
                    const first = bars[firstIndex];
                    const last = bars[lastIndex];
                    const fromReal = realTimeForBar(first);
                    const toReal = realTimeForBar(last);
                    if (Number.isFinite(fromReal) && Number.isFinite(toReal)) {
                        return { from: Math.floor(fromReal), to: Math.floor(toReal) };
                    }
                }
            }

            const range = chartRef.current?.timeScale()?.getVisibleRange?.();
            if (!range) return null;
            const rangeFrom = typeof range.from === 'number' ? range.from : null;
            const rangeTo = typeof range.to === 'number' ? range.to : null;
            if (!Number.isFinite(rangeFrom) || !Number.isFinite(rangeTo)) return null;

            const visibleBars = bars.filter((bar) => {
                const time = Number(bar?.time);
                return Number.isFinite(time) && time >= rangeFrom && time <= rangeTo;
            });

            if (visibleBars.length > 0) {
                const first = visibleBars[0];
                const last = visibleBars[visibleBars.length - 1];
                const fromReal = realTimeForBar(first);
                const toReal = realTimeForBar(last);
                if (Number.isFinite(fromReal) && Number.isFinite(toReal)) {
                    return { from: Math.floor(fromReal), to: Math.floor(toReal) };
                }
            }

            const from = Math.floor(rangeFrom);
            const to = Math.floor(rangeTo);
            if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
            return { from, to };
        } catch {
            return null;
        }
    }, []);

    const extractAnalysisText = useCallback((result) => {
        if (typeof result === 'string') return result;
        if (result && typeof result === 'object') {
            if (typeof result.response === 'string') return result.response;
            if (typeof result.content === 'string') return result.content;
            if (typeof result.message === 'string') return result.message;
            if (typeof result.final_response === 'string') return result.final_response;
            try {
                return JSON.stringify(result, null, 2);
            } catch {
                return String(result);
            }
        }
        return '';
    }, []);

    const pollChartAnalysisExecution = useCallback(async (executionId) => {
        try {
            const execution = await getAgentExecution(executionId);
            const nextStatus = execution?.status || 'RUNNING';

            if (nextStatus === 'COMPLETED') {
                setChartAnalysisState((prev) => ({
                    ...prev,
                    status: 'completed',
                    result: execution?.result || null,
                    error: '',
                }));
                return;
            }

            if (nextStatus === 'ERROR' || nextStatus === 'FAILED' || nextStatus === 'CANCELLED') {
                setChartAnalysisState((prev) => ({
                    ...prev,
                    status: 'error',
                    error: execution?.error || 'Chart analysis did not complete successfully.',
                }));
                return;
            }

            analysisPollTimeoutRef.current = window.setTimeout(() => {
                pollChartAnalysisExecution(executionId);
            }, 2000);
        } catch (error) {
            setChartAnalysisState((prev) => ({
                ...prev,
                status: 'error',
                error: error?.message || 'Could not retrieve chart analysis status.',
            }));
        }
    }, []);

    const handleLaunchChartAnalysis = useCallback(async () => {
        if (!enableStudioChartAnalysis || !selectedSymbol || !selectedTimeframe) return;

        if (analysisPollTimeoutRef.current) {
            clearTimeout(analysisPollTimeoutRef.current);
            analysisPollTimeoutRef.current = null;
        }

        const indicatorsPayload = (Array.isArray(activeIndicators) ? activeIndicators : [])
            .map(normalizeIndicatorPayload)
            .filter(Boolean);
        const payload = {
            symbol: String(selectedSymbol).toUpperCase(),
            timeframe: selectedTimeframe,
            ...(selectedAccount ? { account_id: selectedAccount } : {}),
            indicators: indicatorsPayload,
            visible_range: getCurrentVisibleRange() || undefined,
            theme: 'dark',
            width: chartContainerRef.current?.clientWidth || 1280,
            height: chartContainerRef.current?.clientHeight || height || 720,
        };

        setChartAnalysisModalOpen(true);
        setChartAnalysisState({
            status: 'launching',
            error: '',
            executionId: null,
            sessionId: null,
            preflight: null,
            result: null,
        });

        try {
            const response = await launchStudioChartAnalysis(payload);
            setChartAnalysisState({
                status: 'running',
                error: '',
                executionId: response?.execution_id || null,
                sessionId: response?.session_id || null,
                preflight: response?.preflight || null,
                result: null,
            });

            if (response?.execution_id) {
                pollChartAnalysisExecution(response.execution_id);
                return;
            }

            setChartAnalysisState((prev) => ({
                ...prev,
                status: 'error',
                error: 'Chart analysis started without an execution id.',
            }));
        } catch (error) {
            setChartAnalysisState({
                status: 'error',
                error: error?.message || 'Could not start chart analysis.',
                executionId: null,
                sessionId: null,
                preflight: null,
                result: null,
            });
        }
    }, [
        activeIndicators,
        enableStudioChartAnalysis,
        getCurrentVisibleRange,
        height,
        normalizeIndicatorPayload,
        pollChartAnalysisExecution,
        selectedAccount,
        selectedSymbol,
        selectedTimeframe,
    ]);

    // Init main chart
    // User timezone/locale-aware time formatter
    const prefs = useUserPreferences();
    const { formatDate, formatDateTime } = useDateTimeFormatting();
    const toMs = (t) => {
        if (typeof t === 'number') return t > 1000000000000 ? t : t * 1000; // seconds->ms
        if (t && typeof t === 'object' && typeof t.year === 'number') return new Date(t.year, (t.month || 1) - 1, t.day || 1).getTime();
        return undefined;
    };
    // Axis (tick marks) should be concise; use tickMarkType
    // Resolve compressed time → real time for display
    const axisFmtFn = useMemo(() => (t, tickMarkType) => {
        const real = timeRealMapRef.current.get(t) ?? t;
        const ms = toMs(real);
        if (!ms) return '';
        switch (tickMarkType) {
            // 4: TimeWithSeconds, 3: Time, 2: DayOfMonth, 1: Month, 0: Year (LW charts enum)
            case 4:
                return formatDate?.(ms, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) || '';
            case 3:
                return formatDate?.(ms, { hour: '2-digit', minute: '2-digit' }) || '';
            case 2:
                return formatDate?.(ms, { month: 'short', day: 'numeric' }) || '';
            case 1:
                return formatDate?.(ms, { month: 'short', year: 'numeric' }) || '';
            case 0:
            default:
                return formatDate?.(ms, { year: 'numeric' }) || '';
        }
    }, [formatDate]);
    // Crosshair/tooltip can stay rich
    const crosshairTimeFmt = useMemo(() => (t) => {
        const real = timeRealMapRef.current.get(t) ?? t;
        const ms = toMs(real);
        if (!ms) return '';
        return formatDateTime?.(ms, { second: '2-digit' }) || '';
    }, [formatDateTime]);

    const axisFmtFnRef = useRef(axisFmtFn);
    const crosshairTimeFmtRef = useRef(crosshairTimeFmt);
    const localeRef = useRef(prefs?.locale || 'en-US');
    axisFmtFnRef.current = axisFmtFn;
    crosshairTimeFmtRef.current = crosshairTimeFmt;
    localeRef.current = prefs?.locale || 'en-US';

    useEffect(() => {
        if (!chartContainerRef.current) return;

        const el = chartContainerRef.current;
        const computeSize = () => ({
            width: el.clientWidth || 600,
            height: (typeof heightRef.current === 'number' ? heightRef.current : (el.clientHeight || 350)),
        });
        const { width, height: h } = computeSize();

        const chart = createChart(el, {
            width,
            height: h, // Use computed height
            timeScale: {
                timeVisible: true,
                secondsVisible: true,
                rightOffset: 10,
                lockVisibleTimeRangeOnResize: true,
                rightBarStaysOnScroll: true,
                tickMarkFormatter: (t, tickType /*, localeStr */) => axisFmtFnRef.current(t, tickType),
            },
            localization: { locale: localeRef.current, timeFormatter: (t) => crosshairTimeFmtRef.current(t) },
            handleScroll: {
                mouseWheel: true,
                pressedMouseMove: true,
                horzTouchDrag: true,
                vertTouchDrag: true,
            },
            handleScale: {
                mouseWheel: true,
                pinch: true,
                axisPressedMouseMove: true,
                axisDoubleClickReset: true,
            },
            kineticScroll: { touch: true, mouse: true },
            // Hide attribution logo
            attributionLogo: false,
            layout: { background: { type: ColorType.Solid, color: '#1E293B' }, textColor: '#CBD5E1', attributionLogo: false },
            grid: { vertLines: { color: '#334155' }, horzLines: { color: '#334155' } },
            crosshair: { mode: 0 },
            priceScale: { borderColor: '#334155' },
        });
        chartRef.current = chart;

        const mainSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#22C55E',
            downColor: '#EF4444',
            borderVisible: false,
            wickUpColor: '#22C55E',
            wickDownColor: '#EF4444',
        });
        seriesRef.current = mainSeries;
        const positionRiskLines = positionRiskLinesRef.current;
        const supportResistanceLines = supportResistanceLinesRef.current;
        const indicatorSeries = indicatorSeriesRef.current;
        const indicatorSparseMarkers = indicatorSparseMarkersRef.current;
        const indicatorEventMarkers = indicatorEventMarkersRef.current;
        const indicatorEventSegments = indicatorEventSegmentsRef.current;
        const indicatorGuideLines = indicatorGuideLinesRef.current;
        const specSeries = specSeriesRef.current;
        const backtestIndicatorLines = backtestIndicatorLineRefs.current;
        const backtestSeriesKinds = backtestSeriesKindRef.current;
        const backtestIndicatorMarkers = backtestIndicatorMarkersRef.current;
        const backtestIndicatorSegments = backtestIndicatorSegmentsRef.current;
        const backtestIndicatorGuides = backtestIndicatorGuidesRef.current;
        const volumeOverlaySeries = volumeOverlaySeriesRef.current;

    // Do NOT create volume series here; create it lazily when we actually have volume data

        // Subscribe to clicks after chart + series are ready
        let clickHandler = null;
        clickHandler = (param) => {
            let idx = null;
            // 1) Prefer logical index when present
            if (typeof param?.logical === 'number' && Number.isFinite(param.logical)) {
                const maxIndex = Math.max(0, (barsRef.current?.length || 0) - 1);
                const rounded = Math.min(maxIndex, Math.max(0, Math.round(param.logical)));
                idx = rounded;
            }
            // 2) Fallback to time -> index map
            if (idx == null && typeof param?.time === 'number') {
                const fromTime = timeToIndexRef.current.get(param.time);
                if (fromTime != null) idx = fromTime;
            }
            // 3) Fallback to seriesData map
            if (idx == null && param?.seriesData && seriesRef.current) {
                const main = param.seriesData.get?.(seriesRef.current);
                const t = main && typeof main.time === 'number' ? main.time : null;
                if (t != null) {
                    const maybe = timeToIndexRef.current.get(t);
                    if (maybe != null) idx = maybe;
                }
            }
            if (idx != null) {
                try { onBarSelectedRef.current && onBarSelectedRef.current(idx); } catch { /* ignore */ }
            }
        };
    try { chart.subscribeClick(clickHandler); } catch { /* ignore */ }

        const applyResize = () => {
            const { width, height: h2 } = computeSize();
            chart.applyOptions({ width, height: h2 });
        };

        // Observe container size changes
        let ro;
        try {
            ro = new ResizeObserver(() => applyResize());
            ro.observe(el);
    } catch { /* ignore */ }

    // Fallback to window resize
    window.addEventListener('resize', applyResize);

        return () => {
            window.removeEventListener('resize', applyResize);
            try { ro && ro.disconnect(); } catch { /* ignore */ }
            try { if (clickHandler) chart.unsubscribeClick(clickHandler); } catch { /* ignore */ }
            try { chart.remove(); } catch { /* ignore */ }

            if (chartRef.current === chart) chartRef.current = null;
            if (seriesRef.current === mainSeries) seriesRef.current = null;
            volumeSeriesRef.current = null;
            volumePaneIndexRef.current = null;
            markersRef.current = null;
            recentTradeMarkersRef.current = [];
            stopLossLineRef.current = null;
            takeProfitLineRef.current = null;
            positionRiskLines.clear();
            supportResistanceLines.clear();
            indicatorSeries.clear();
            indicatorSparseMarkers.clear();
            indicatorEventMarkers.clear();
            indicatorEventSegments.clear();
            indicatorGuideLines.clear();
            specSeries.clear();
            backtestIndicatorLines.clear();
            backtestSeriesKinds.clear();
            backtestIndicatorMarkers.clear();
            backtestIndicatorSegments.clear();
            backtestIndicatorGuides.clear();
            volumeOverlaySeries.clear();
        };
    }, []);

    // ── Drawing tools: attach / detach manager on chart (re)init ─────────────
    useEffect(() => {
        const chart = chartRef.current;
        const series = seriesRef.current;
        const canvas = drawingCanvasRef.current;
        if (!chart || !series || !canvas) return;

        if (!drawingManagerRef.current) {
            drawingManagerRef.current = new DrawingToolsManager();
        }
        const mgr = drawingManagerRef.current;
        mgr.onChange = () => {
            setDrawingSelection(!!mgr._selectedId);
            setDrawingCount(mgr.drawings.length);
            setDrawings([...mgr.drawings]);
            setSelectedDrawingId(mgr._selectedId || null);
        };
        mgr.attach(canvas, chart, series, selectedSymbol, selectedTimeframe);
        setDrawingCount(mgr.drawings.length);
        setDrawings([...mgr.drawings]);

        return () => {
            mgr.detach();
        };
    }, [selectedSymbol, selectedTimeframe]);

    // Keep drawing manager in sync with tool selection
    useEffect(() => {
        drawingManagerRef.current?.setTool(activeDrawingTool);
    }, [activeDrawingTool]);

    // Keep drawing manager in sync with symbol/timeframe changes
    useEffect(() => {
        if (drawingManagerRef.current) {
            drawingManagerRef.current.setContext(selectedSymbol, selectedTimeframe);
            setDrawingCount(drawingManagerRef.current.drawings.length);
            setDrawings([...drawingManagerRef.current.drawings]);
        }
    }, [selectedSymbol, selectedTimeframe]);

    // Expose a way to re-render drawings when chart scrolls/zooms
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const handler = () => drawingManagerRef.current?.markDirty();
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); } catch { /* ignore */ }
        return () => {
            try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch { /* ignore */ }
        };
    }, []);

    const handleSelectDrawingTool = useCallback((tool) => {
        setActiveDrawingTool(tool);
    }, []);

    const handleDeleteSelectedDrawing = useCallback(() => {
        drawingManagerRef.current?.deleteSelected();
        setDrawingSelection(false);
        setDrawingCount(drawingManagerRef.current?.drawings.length || 0);
    }, []);

    const handleDeleteAllDrawings = useCallback(() => {
        drawingManagerRef.current?.deleteAll();
        setDrawingSelection(false);
        setDrawingCount(0);
        setDrawings([]);
        setSelectedDrawingId(null);
    }, []);

    const handleUpdateDrawing = useCallback((id, props) => {
        drawingManagerRef.current?.updateDrawing(id, props);
        setDrawings([...(drawingManagerRef.current?.drawings || [])]);
    }, []);

    const handleSelectDrawing = useCallback((id) => {
        drawingManagerRef.current?.selectDrawing(id);
        setSelectedDrawingId(id);
        setDrawingSelection(!!id);
    }, []);

    // Publish pane tops on the shared overlay host without causing a React render.
    const recomputePaneTops = useCallback(() => {
        const chartHost = chartContainerRef.current;
        const overlayHost = paneOverlayHostRef.current;
        if (!chartHost || !overlayHost) return;

        const overlayRect = overlayHost.getBoundingClientRect();
        const chartRect = chartHost.getBoundingClientRect();
        const chartTop = Math.round(chartRect.top - overlayRect.top);
        let offsets = [];

        try {
            const panes = chartRef.current?.panes?.() || [];
            let nextTop = chartTop;
            if (panes.length > 0) {
                offsets = panes.map((pane) => {
                    const top = nextTop;
                    const height = Number(pane?.getHeight?.());
                    if (!Number.isFinite(height) || height < 0) throw new Error('Invalid pane height');
                    nextTop += height;
                    return top;
                });
            }
        } catch {
            offsets = [];
        }

        // Retain a DOM fallback for chart builds that do not expose pane heights.
        if (offsets.length === 0) {
            const seen = new Set();
            offsets = Array.from(chartHost.querySelectorAll('canvas'))
                .map((canvas) => Math.round(canvas.getBoundingClientRect().top - overlayRect.top))
                .filter((top) => {
                    if (!Number.isFinite(top) || top < 0 || top >= overlayHost.clientHeight || seen.has(top)) {
                        return false;
                    }
                    seen.add(top);
                    return true;
                })
                .sort((a, b) => a - b);
        }

        offsets.forEach((top, index) => {
            overlayHost.style.setProperty(`--chart-pane-top-${index}`, `${top}px`);
            overlayHost.style.setProperty(`--chart-pane-visibility-${index}`, 'visible');
        });
        for (let index = offsets.length; index < paneTopPropertyCountRef.current; index += 1) {
            overlayHost.style.removeProperty(`--chart-pane-top-${index}`);
            overlayHost.style.removeProperty(`--chart-pane-visibility-${index}`);
        }
        paneTopPropertyCountRef.current = offsets.length;
    }, []);

    const schedulePaneTopsRecompute = useCallback(() => {
        if (paneMeasureTimerRef.current != null) return;
        paneMeasureTimerRef.current = setTimeout(() => {
            paneMeasureTimerRef.current = null;
            recomputePaneTops();
        }, 0);
    }, [recomputePaneTops]);

    // Keep pane positions updated
    useEffect(() => {
        const host = chartContainerRef.current;
        if (!host) return undefined;
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(schedulePaneTopsRecompute)
            : null;
        resizeObserver?.observe(host);
        const onResize = schedulePaneTopsRecompute;
        window.addEventListener('resize', onResize);
        schedulePaneTopsRecompute();
        return () => {
            try { resizeObserver?.disconnect(); } catch { /* ignore */ }
            window.removeEventListener('resize', onResize);
            if (paneMeasureTimerRef.current != null) {
                clearTimeout(paneMeasureTimerRef.current);
                paneMeasureTimerRef.current = null;
            }
        };
    }, [paneLayoutKey, schedulePaneTopsRecompute]);

    // Lazy-load older candles on left scroll edge
    useEffect(() => {
        if (isBacktest && !loadMoreHistory) return;
        const chart = chartRef.current;
        if (!chart || !seriesRef.current) return;

        let detach = null;
        let debouncing = false;
        const timeScale = chart.timeScale();

        const ensureLeftData = async () => {
            if (debouncing) return;
            debouncing = true;
            try {
                // Snapshot current left bar index and from/to
                const range = timeScale.getVisibleLogicalRange?.();
                const beforeDataLen = barsRef.current?.length || 0;
                const res = await (loadMoreHistory ? loadMoreHistory(500) : Promise.resolve({ added: 0, total: beforeDataLen }));
                const added = Number(res?.added) || 0;
                if (added > 0) {
                    // Store the pre-load visible range + old bar count so the
                    // historicalData effect can compute the exact index shift after
                    // setData (which accounts for dedup/compression differences).
                    try {
                        const from = range?.from;
                        const to = range?.to;
                        if (typeof from === 'number' && typeof to === 'number') {
                            pendingScrollRestoreRef.current = {
                                from,
                                to,
                                oldLen: beforeDataLen,
                                contextKey: chartDataContextRef.current,
                            };
                        }
                    } catch { /* ignore */ }
                }
            } finally {
                // small delay to avoid thrashing
                setTimeout(() => { debouncing = false; }, 200);
            }
        };

        const onRangeChange = () => {
            try {
                const range = timeScale.getVisibleLogicalRange?.();
                if (!range) return;
                const from = range.from;
                const barsLoaded = barsRef.current?.length || 0;
                // When user scrolls near the first bar, range.from approaches 0
                // Trigger load when within 5 bars of the left edge
                if (typeof from === 'number' && from < 5 && barsLoaded >= 1) {
                    ensureLeftData();
                }
            } catch { /* ignore */ }
        };

        try {
            timeScale.subscribeVisibleLogicalRangeChange(onRangeChange);
            detach = () => timeScale.unsubscribeVisibleLogicalRangeChange(onRangeChange);
    } catch { /* ignore */ }

    return () => { try { detach && detach(); } catch { /* ignore */ } };
    }, [isBacktest, loadMoreHistory]);

    // React to timezone/locale changes by updating chart localization
    useEffect(() => {
        if (!chartRef.current) return;
        chartRef.current.applyOptions({
            localization: { locale: prefs?.locale || 'en-US', timeFormatter: (t) => crosshairTimeFmt(t) },
            timeScale: { tickMarkFormatter: (t, tickType) => axisFmtFn(t, tickType) },
        });
    }, [prefs?.locale, prefs?.timezone, axisFmtFn, crosshairTimeFmt]);

    useEffect(() => {
        const chart = chartRef.current;
        const el = chartContainerRef.current;
        if (!chart || !el) return;
        chart.applyOptions({
            width: el.clientWidth || 600,
            height: typeof height === 'number' ? height : (el.clientHeight || 350),
        });
    }, [height]);

    // Helper to lazily create/remove the volume histogram series
    const ensureVolumeSeries = useCallback(() => {
        if (!showVolume) return null;
        const chart = chartRef.current;
        if (!chart) return null;
        // Assign a dedicated pane index for volume if not yet set
        if (volumePaneIndexRef.current == null) {
            // Reserve pane index 1 for volume; existing pane indicators (backtest) will start from 2
            volumePaneIndexRef.current = 1;
        }
        if (!volumeSeriesRef.current) {
            const paneIndex = volumePaneIndexRef.current;
            const vs = chart.addSeries(HistogramSeries, {
                priceScaleId: 'volume',
                priceFormat: { type: 'volume' },
                color: '#22C55E',
            }, paneIndex);
            // Try setting pane height modestly
            try { vs.getPane?.().setHeight?.(80); } catch { /* ignore */ }
            // Configure scale
            try {
                const volScale = chart.priceScale('volume');
                volScale?.applyOptions({ visible: false, borderVisible: false, scaleMargins: { top: 0.05, bottom: 0 } });
            } catch { /* ignore */ }
            volumeSeriesRef.current = vs;
        }
        return volumeSeriesRef.current;
    }, [showVolume]);
    const removeVolumeSeries = useCallback(() => {
        try {
            if (chartRef.current && volumeSeriesRef.current) {
                chartRef.current.removeSeries(volumeSeriesRef.current);
                volumeSeriesRef.current = null;
            }
    } catch { /* ignore */ }
    }, []);

    // Historical Data (Live or Backtest)
    useEffect(() => {
        if (!seriesRef.current) return;

        // When historical data changes (new symbol/timeframe), do not clear indicators —
        // we preserve and will reapply visual overrides via cache.

        const dataToUse = isBacktest ? convertOhlcvToBars(ohlcvData) : historicalData;

        // Only pass complete finite OHLC bars to lightweight-charts. Its internal
        // marker and scale calculations assume every rendered bar has a value.
        const sanitized = Array.isArray(dataToUse)
            ? dataToUse.map(b => {
                const o = toFiniteNumber(b?.open);
                const h = toFiniteNumber(b?.high);
                const l = toFiniteNumber(b?.low);
                const c = toFiniteNumber(b?.close);
                const t = b?.time;
                const realTime = toFiniteNumber(b?.time_real);
                if (!Number.isFinite(t) || [o, h, l, c].some((value) => value === undefined)) return null;
                return {
                    time: t,
                    ...(Number.isFinite(realTime) && realTime !== t ? { time_real: realTime } : {}),
                    open: o,
                    high: h,
                    low: l,
                    close: c,
                };
            }).filter(Boolean).sort((a, b) => a.time - b.time)
            : [];

        // Deduplicate by timestamp — lightweight-charts requires strictly ascending time.
        // Keep the last bar for each timestamp (most recent update wins).
        const deduped = [];
        for (let i = 0; i < sanitized.length; i++) {
            if (i + 1 < sanitized.length && sanitized[i].time === sanitized[i + 1].time) continue;
            deduped.push(sanitized[i]);
        }

        if (deduped.length === 0) {
            clearMarkerPrimitives();
            try {
                seriesRef.current.setData([]);
                setChartRenderError(null);
            } catch (error) {
                console.error('Failed to clear chart series safely', error);
                setChartRenderError('The chart could not be reset. Please reload and try again.');
            }
            // clear refs
            barsRef.current = [];
            timeToIndexRef.current = new Map();
            mainSeriesStateRef.current = {
                contextKey: chartDataContextRef.current,
                lastTime: null,
            };
            // Also clear volume if present
            removeVolumeSeries();
            return;
        }

        try {
            seriesRef.current.setData(deduped);
            setChartRenderError(null);
        } catch (error) {
            console.error('Failed to apply chart history safely', error);
            setChartRenderError('The chart data could not be displayed. Please try again.');
            return;
        }

        // Restore scroll position after backfill prepend
        if (pendingScrollRestoreRef.current && chartRef.current) {
            const { from, to, oldLen, contextKey } = pendingScrollRestoreRef.current;
            pendingScrollRestoreRef.current = null;
            // Compute exact shift = how many bars were actually prepended in the
            // deduped array (handles compression/dedup differences).
            const shift = deduped.length - oldLen;
            if ((!contextKey || contextKey === chartDataContextRef.current) && shift > 0) {
                const ts = chartRef.current.timeScale();
                // Use rAF so lightweight-charts finishes its internal layout first
                requestAnimationFrame(() => {
                    if (contextKey && contextKey !== chartDataContextRef.current) return;
                    try {
                        ts.setVisibleLogicalRange({ from: from + shift, to: to + shift });
                    } catch { /* ignore */ }
                });
            }
        }

        // cache for quick lookup
        barsRef.current = deduped;
        const map = new Map();
        for (let i = 0; i < deduped.length; i++) {
            const t = deduped[i]?.time;
            if (typeof t === 'number') map.set(t, i);
        }
        timeToIndexRef.current = map;
        mainSeriesStateRef.current = {
            contextKey: chartDataContextRef.current,
            lastTime: deduped[deduped.length - 1].time,
        };
        // chartRef.current.timeScale().fitContent();

        // Volume mapping: prefer explicit volume in source data when available
        const toSecondsSafe = (t) => (typeof t === 'number' ? (t > 1000000000000 ? Math.floor(t / 1000) : t) : undefined);
        let volumePoints = [];
        if (isBacktest && Array.isArray(ohlcvData)) {
            // Pull volume from backtest OHLCV if present. Accept several possible field names.
            volumePoints = ohlcvData
                .map(d => {
                    if (!d) return null;
                    const t = toSecondsSafe(d.time);
                    if (!Number.isFinite(t)) return null;
                    const rawVol = d.volume ?? d.vol ?? d.tick_volume ?? d.tickVolume;
                    if (!Number.isFinite(rawVol)) return null;
                    const isUp = Number(d.close) >= Number(d.open);
                    return { time: t, value: Number(rawVol), color: isUp ? '#22C55E' : '#EF4444' };
                })
                .filter(Boolean)
                .sort((a, b) => a.time - b.time);
        } else if (Array.isArray(historicalData) && historicalData.length > 0) {
            // Some backends may include volume on historicalData in the future
            const hasVol = historicalData.some(b => Number.isFinite(b?.volume) || Number.isFinite(b?.tickVolume));
            if (hasVol) {
                const map = new Map();
                sanitized.forEach((bar) => { if (typeof bar?.time === 'number') map.set(bar.time, bar); });
                volumePoints = historicalData
                    .map((b) => {
                        const t = b?.time;
                        const v = Number.isFinite(b?.volume) ? Number(b.volume) : (Number.isFinite(b?.tickVolume) ? Number(b.tickVolume) : undefined);
                        if (!Number.isFinite(v) || typeof t !== 'number') return null;
                        const bar = map.get(t);
                        const isUp = bar && Number.isFinite(bar?.open) && Number.isFinite(bar?.close) ? (bar.close >= bar.open) : true;
                        return { time: t, value: v, color: isUp ? '#22C55E' : '#EF4444' };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.time - b.time);
            }
        }

        if (showVolume && volumePoints.length > 0) {
            const vs = ensureVolumeSeries();
            try { vs.setData(volumePoints); } catch { /* ignore */ }
        } else {
            // No volume available; remove pane if exists (and any overlays)
            removeVolumeSeries();
            try {
                volumeOverlaySeriesRef.current.forEach((s) => { try { chartRef.current?.removeSeries(s); } catch { /* ignore */ } });
                volumeOverlaySeriesRef.current.clear();
                setBacktestVolumeOverlayLabels([]);
            } catch { /* ignore */ }
        }

    }, [historicalData, ohlcvData, isBacktest, showVolume, ensureVolumeSeries, removeVolumeSeries, clearMarkerPrimitives]);

    // Scroll to selected trade
    useEffect(() => {
        if (!chartRef.current || !selectedTrade) return;
    
        const tradeTimeInSeconds = Math.floor(new Date(selectedTrade.entry_timestamp).getTime() / 1000);
        
        // Define a window around the trade time, e.g., 1 hour before and after
        const windowInSeconds = 60 * 60; 
        const from = tradeTimeInSeconds - windowInSeconds;
        const to = tradeTimeInSeconds + windowInSeconds;

        chartRef.current.timeScale().setVisibleRange({ from, to });

    }, [selectedTrade]);

    // Live updates (only for live trading)
    useEffect(() => {
        if (!isBacktest && seriesRef.current && candle) {
            const contextKey = chartDataContextRef.current;
            if (candle.feedContextKey && candle.feedContextKey !== contextKey) return;
            if (mainSeriesStateRef.current.contextKey !== contextKey) return;

            // Compare with the series itself, not React history, which intentionally
            // lags during incremental same-bar updates.
            if (!Number.isFinite(candle.time)) return;
            const lastSeriesTime = mainSeriesStateRef.current.lastTime;
            if (typeof lastSeriesTime === 'number' && candle.time < lastSeriesTime) return;
            const o = toFiniteNumber(candle?.open);
            const h = toFiniteNumber(candle?.high);
            const l = toFiniteNumber(candle?.low);
            const c = toFiniteNumber(candle?.close);
            if ([o, h, l, c].some((value) => value === undefined)) {
                console.warn('Dropped live candle without finite OHLC values', { contextKey, candleTime: candle.time });
                return;
            }
            const realTime = Number(candle.time_real);
            if (Number.isFinite(realTime) && realTime !== candle.time) {
                timeRealMapRef.current.set(candle.time, realTime);
            }
            const payload = { time: candle.time, open: o, high: h, low: l, close: c };
            try {
                seriesRef.current.update(payload);
            } catch (error) {
                console.warn('Dropped live candle rejected by chart series', {
                    contextKey,
                    candleTime: candle.time,
                    lastSeriesTime,
                    error,
                });
                return;
            }

            const trackedBar = {
                ...payload,
                ...(Number.isFinite(Number(candle.time_real)) ? { time_real: Number(candle.time_real) } : {}),
            };
            const currentBars = Array.isArray(barsRef.current) ? barsRef.current : [];
            const lastIndex = currentBars.length - 1;
            if (lastIndex >= 0 && currentBars[lastIndex]?.time === candle.time) {
                barsRef.current = [...currentBars.slice(0, lastIndex), trackedBar];
                timeToIndexRef.current.set(candle.time, lastIndex);
            } else {
                barsRef.current = [...currentBars, trackedBar];
                timeToIndexRef.current.set(candle.time, currentBars.length);
            }
            mainSeriesStateRef.current = { contextKey, lastTime: candle.time };

            // Update volume if present on live candle
            const vol = Number.isFinite(candle?.volume) ? Number(candle.volume) : (Number.isFinite(candle?.tickVolume) ? Number(candle.tickVolume) : (Number.isFinite(candle?.tick_volume) ? Number(candle.tick_volume) : undefined));
        if (showVolume && Number.isFinite(vol)) {
                const vs = ensureVolumeSeries(0);
                const isUp = c >= o;
                try { vs.update({ time: candle.time, value: vol, color: isUp ? '#22C55E' : '#EF4444' }); } catch { /* ignore */ }
            }
            // Do not auto-center per tick; only center once after initial load/connection
        }
    }, [candle, isBacktest, historicalData, showVolume, ensureVolumeSeries]);

    // Live indicator point updates — lightweight .update() calls (like candle live updates)
    // This avoids the heavy full-render indicator useEffect on every tick.
    useEffect(() => {
        if (!indicatorLiveUpdate || !chartRef.current || isBacktest) return;
        const { id: frontendId, point, data_keys } = indicatorLiveUpdate;
        const realTime = point.time;

        // Find compressed time from historicalData (last or second-to-last bar)
        const last = historicalData[historicalData.length - 1];
        const secondToLast = historicalData.length >= 2 ? historicalData[historicalData.length - 2] : null;
        let compressedTime = Number.isFinite(indicatorLiveUpdate.chart_time)
            ? indicatorLiveUpdate.chart_time
            : realTime;
        if (!Number.isFinite(indicatorLiveUpdate.chart_time) && last && (last.time_real ?? last.time) === realTime) {
            compressedTime = last.time;
        } else if (!Number.isFinite(indicatorLiveUpdate.chart_time) && secondToLast && (secondToLast.time_real ?? secondToLast.time) === realTime) {
            compressedTime = secondToLast.time;
        }
        if (Number.isFinite(compressedTime) && Number.isFinite(realTime) && compressedTime !== realTime) {
            timeRealMapRef.current.set(compressedTime, realTime);
        }

        const currentSeries = indicatorSeriesRef.current;

        // Helper to get value for a key from the point data
        const valueFor = (k) => {
            if (!k) return undefined;
            if (Object.prototype.hasOwnProperty.call(point, k)) return point[k];
            if (point.value && typeof point.value === 'object' && Object.prototype.hasOwnProperty.call(point.value, k)) return point.value[k];
            if (point.outputs && typeof point.outputs === 'object' && Object.prototype.hasOwnProperty.call(point.outputs, k)) return point.outputs[k];
            return undefined;
        };

        if (Array.isArray(data_keys) && data_keys.length > 1) {
            // Multi-output: update each series
            data_keys.forEach(key => {
                const seriesId = `${frontendId}-${key}`;
                const entry = currentSeries.get(seriesId);
                if (!entry?.series) return;
                const v = valueFor(key);
                if (!Number.isFinite(v)) return;
                const [styledPoint] = applySeriesPointVisuals(
                    [{ time: compressedTime, value: v }],
                    entry.plotType,
                    entry.pointVisualOptions,
                );
                try { entry.series.update(styledPoint); } catch { /* ignore */ }
            });
        } else {
            // Single output
            let value = undefined;
            if (Array.isArray(data_keys) && data_keys.length > 0) {
                value = valueFor(data_keys[0]);
            }
            if (value == null && Number.isFinite(point.value)) value = point.value;
            if (value == null) {
                for (const k of Object.keys(point)) {
                    if (k === 'time') continue;
                    if (Number.isFinite(point[k])) { value = point[k]; break; }
                }
            }
            if (Number.isFinite(value)) {
                let entry = currentSeries.get(frontendId);
                if (!entry?.series && Array.isArray(data_keys) && data_keys[0]) {
                    entry = currentSeries.get(`${frontendId}-${data_keys[0]}`);
                }
                if (entry?.series) {
                const [styledPoint] = applySeriesPointVisuals(
                    [{ time: compressedTime, value }],
                    entry.plotType,
                    entry.pointVisualOptions,
                );
                try { entry.series.update(styledPoint); } catch { /* ignore */ }
                }
            }
        }
    }, [indicatorLiveUpdate, isBacktest, historicalData]);

    // Move to the live edge once per context as soon as the initial history renders.
    // Waiting for the socket used to cause a delayed viewport jump after users had
    // already started scrolling or zooming.
    useEffect(() => {
        const key = `${selectedAccount || ''}|${selectedSymbol || ''}|${selectedTimeframe || ''}`;
        // Reset flag when identity changes
        if (centeredContextKeyRef.current !== key) {
            centeredContextKeyRef.current = key;
            didCenterRef.current = false;
        }
        if (!chartRef.current) return;
        if (props?.selectedTrade) return; // respect explicit trade focus
        const histLen = Array.isArray(historicalData) ? historicalData.length : 0;
        if (histLen <= 0) return;
        if (!didCenterRef.current) {
            // Slight delay to ensure any initial indicators/layout settle
            const t = setTimeout(() => {
                if (centerOnLastBar()) didCenterRef.current = true;
            }, 0);
            return () => clearTimeout(t);
        }
    }, [historicalData, selectedAccount, selectedSymbol, selectedTimeframe, centerOnLastBar, props?.selectedTrade]);

    // Tick/pip/digits sizing -> controls price axis precision and step
    useEffect(() => {
        if (!seriesRef.current) return;

        // Parse numeric values safely
        const t = (tickSize != null && !Number.isNaN(Number(tickSize)) && Number(tickSize) > 0)
            ? Number(tickSize) : undefined;
        const p = (pipSize != null && !Number.isNaN(Number(pipSize)) && Number(pipSize) > 0)
            ? Number(pipSize) : undefined;
        // Lookup cached digits if prop not provided
        const cachedDigits = digits ?? getDigitsFor(selectedAccount, selectedSymbol);
        const hasDigits = Number.isFinite(cachedDigits);

        // Choose a reasonable minMove; prefer the smaller positive step
        const minMove = (t && p) ? Math.min(t, p) : (t || p);
        // If we have digits but no minMove known, we can still apply precision

        // Derive precision; prefer pipSize when available for better digit depth
        const basis = p || minMove;
        const getPrecision = (val) => {
            if (!Number.isFinite(val)) return 0;
            // Avoid issues with float repr; increment multiplier until integer or cap
            let e = 1, digits = 0;
            while (Math.round(val * e) / e !== val && digits < 10) {
                e *= 10;
                digits += 1;
            }
            return digits;
        };
        const precision = hasDigits ? Number(cachedDigits) : getPrecision(basis);

        // If this is a backtest and precision ended up 0 (or very low) but symbol looks like FX, derive standard FX digits
        let fxAdjustedPrecision = precision;
        if (isBacktest && (!Number.isFinite(fxAdjustedPrecision) || fxAdjustedPrecision < 2)) {
            const sym = (selectedSymbol || '').toUpperCase();
            // Basic heuristic: 6 chars alpha and not containing '/': treat as XXXYYY FX
            if (/^[A-Z]{6,7}$/.test(sym)) {
                // JPY pairs usually 3 decimals (0.001) for pip, some brokers 2; else 5 decimals typical
                if (sym.includes('JPY')) fxAdjustedPrecision = 3; else fxAdjustedPrecision = 5;
            } else if (sym.includes('/') && sym.replace('/', '').length === 6) {
                // Format like EUR/USD
                if (sym.includes('JPY')) fxAdjustedPrecision = 3; else fxAdjustedPrecision = 5;
            } else {
                // Fallback: infer from recent close price magnitude if available via barsRef
                try {
                    const last = barsRef.current && barsRef.current.length ? barsRef.current[barsRef.current.length - 1] : null;
                    const refPrice = last ? last.close : undefined;
                    if (Number.isFinite(refPrice)) {
                        if (refPrice < 0.1) fxAdjustedPrecision = 6; else if (refPrice < 1) fxAdjustedPrecision = 5; else if (refPrice < 10) fxAdjustedPrecision = 4; else fxAdjustedPrecision = 2;
                    }
                } catch { /* ignore */ }
            }
        }

        const priceFormat = { type: 'price', precision: fxAdjustedPrecision };
        if (Number.isFinite(minMove)) {
            priceFormat.minMove = minMove;
        } else if (hasDigits && Number.isFinite(fxAdjustedPrecision) && fxAdjustedPrecision >= 0 && fxAdjustedPrecision <= 12) {
            priceFormat.minMove = Math.pow(10, -fxAdjustedPrecision);
        } else if (isBacktest && Number.isFinite(fxAdjustedPrecision)) {
            priceFormat.minMove = Math.pow(10, -fxAdjustedPrecision);
        }
        seriesRef.current.applyOptions({ priceFormat });
    }, [tickSize, pipSize, digits, selectedAccount, selectedSymbol, isBacktest]);

    // Stop Loss Line (only for live trading)
    useEffect(() => {
        const series = seriesRef.current;
        if (!chartRef.current || !series) return;
        const price = Number.parseFloat(stopLossPrice);

        if (!isBacktest && Number.isFinite(price) && price > 0) {
            if (stopLossLineRef.current) {
                try {
                    stopLossLineRef.current.applyOptions({ price });
                    return;
                } catch {
                    stopLossLineRef.current = null;
                }
            }
            try {
                stopLossLineRef.current = series.createPriceLine({
                    price,
                    color: '#EF4444', // Red for Stop Loss
                    lineWidth: 2,
                    lineStyle: LineStyle.Dashed,
                    axisLabelVisible: true,
                    title: 'SL',
                });
            } catch {
                stopLossLineRef.current = null;
            }
            return;
        }

        if (stopLossLineRef.current) {
            try { series.removePriceLine(stopLossLineRef.current); } catch { /* ignore */ }
            stopLossLineRef.current = null;
        }
    }, [stopLossPrice, isBacktest]);

    // Take Profit Line (only for live trading)
    useEffect(() => {
        const series = seriesRef.current;
        if (!chartRef.current || !series) return;
        const price = Number.parseFloat(takeProfitPrice);

        if (!isBacktest && Number.isFinite(price) && price > 0) {
            if (takeProfitLineRef.current) {
                try {
                    takeProfitLineRef.current.applyOptions({ price });
                    return;
                } catch {
                    takeProfitLineRef.current = null;
                }
            }
            try {
                takeProfitLineRef.current = series.createPriceLine({
                    price,
                    color: '#22C55E', // Green for Take Profit
                    lineWidth: 2,
                    lineStyle: LineStyle.Dashed,
                    axisLabelVisible: true,
                    title: 'TP',
                });
            } catch {
                takeProfitLineRef.current = null;
            }
            return;
        }

        if (takeProfitLineRef.current) {
            try { series.removePriceLine(takeProfitLineRef.current); } catch { /* ignore */ }
            takeProfitLineRef.current = null;
        }
    }, [takeProfitPrice, isBacktest]);

    // Risk levels from active broker positions follow the trades overlay toggle.
    useEffect(() => {
        const series = seriesRef.current;
        if (!chartRef.current || !series) return;

        const desired = !isBacktest && showRecentTrades
            ? new Map(buildPositionRiskLevels(openPositions, selectedSymbol).map((level) => [level.key, level]))
            : new Map();

        positionRiskLinesRef.current.forEach((entry, key) => {
            if (desired.has(key)) return;
            try { series.removePriceLine(entry.line); } catch { /* ignore */ }
            positionRiskLinesRef.current.delete(key);
        });

        desired.forEach((level, key) => {
            const existing = positionRiskLinesRef.current.get(key);
            if (existing && existing.price === level.price && existing.title === level.title) return;

            if (existing) {
                try {
                    existing.line.applyOptions({ price: level.price, title: level.title });
                    positionRiskLinesRef.current.set(key, { ...existing, price: level.price, title: level.title });
                    return;
                } catch {
                    try { series.removePriceLine(existing.line); } catch { /* ignore */ }
                    positionRiskLinesRef.current.delete(key);
                }
            }

            try {
                const line = series.createPriceLine({
                    price: level.price,
                    color: level.type === 'sl' ? '#F87171' : '#4ADE80',
                    lineWidth: 1,
                    lineStyle: LineStyle.Dotted,
                    axisLabelVisible: true,
                    title: level.title,
                });
                positionRiskLinesRef.current.set(key, {
                    line,
                    price: level.price,
                    title: level.title,
                });
            } catch { /* ignore */ }
        });
    }, [openPositions, selectedSymbol, isBacktest, showRecentTrades]);

    // Support/Resistance Price Lines
    useEffect(() => {
        if (!chartRef.current || !seriesRef.current || isBacktest) return;
        
        const mainSeries = seriesRef.current;
        
        // Find all support_resistance indicators (regardless of paneType)
        const srIndicators = activeIndicators.filter(isSupportResistanceIndicator);
        
        // Track which indicators are active
        const activeIds = new Set(srIndicators.map(ind => ind.id));
        
        // Remove price lines for indicators that are no longer active
        supportResistanceLinesRef.current.forEach((lines, indicatorId) => {
            if (!activeIds.has(indicatorId)) {
                lines.forEach(line => {
                    try { mainSeries.removePriceLine(line); } catch { /* ignore */ }
                });
                supportResistanceLinesRef.current.delete(indicatorId);
            }
        });
        
        // Process each active support_resistance indicator
        srIndicators.forEach(indicator => {
            if (!indicator.data) {
                return;
            }

            const indicatorMeta = getIndicatorMeta(availableIndicators, indicator);
            const visualDefaults = indicatorMeta?.visual_defaults || {};
            const overrides = indicator?.visual_overrides || {};
            const visualsByKey = deepMerge(visualDefaults, overrides)?.series || {};
            
            // Data can be either an object (single update) or array of objects
            let data = indicator.data;
            
            // If it's an array, get the latest data point
            if (Array.isArray(data)) {
                if (data.length === 0) {
                    return;
                }
                data = data[data.length - 1];
            }
            
            // If the data point has nested structure, extract it
            if (data && typeof data === 'object' && !data.support_resistance_support_1) {
                // Check if data is in data.data or data.value or data.outputs
                if (data.data && typeof data.data === 'object') {
                    data = data.data;
                } else if (data.value && typeof data.value === 'object') {
                    data = data.value;
                } else if (data.outputs && typeof data.outputs === 'object') {
                    data = data.outputs;
                }
            }
            
            // Helper function to get the latest value from an array or return the value as-is
            const getLatestValue = (val) => {
                if (Array.isArray(val) && val.length > 0) {
                    // Get the last non-null/non-undefined value
                    for (let i = val.length - 1; i >= 0; i--) {
                        const item = val[i];
                        if (item == null) continue;
                        // If it's an object with a value property, extract it
                        if (typeof item === 'object' && item.value != null) {
                            return item.value;
                        }
                        // If it's already a scalar, return it
                        if (typeof item === 'number') {
                            return item;
                        }
                    }
                    return null;
                }
                return val;
            };
            
            // Extract support and resistance levels
            const levels = [];
            
            // Support levels (1-5)
            for (let i = 1; i <= 5; i++) {
                const priceKey = `support_resistance_support_${i}`;
                const touchesKey = `support_resistance_support_touches_${i}`;
                const strengthKey = `support_resistance_support_strength_${i}`;
                
                const priceRaw = data[priceKey];
                const touchesRaw = data[touchesKey];
                const strengthRaw = data[strengthKey];
                
                const price = getLatestValue(priceRaw);
                const touches = getLatestValue(touchesRaw);
                const strength = getLatestValue(strengthRaw);
                const visual = pickSeriesVisual(visualsByKey, indicatorMeta, priceKey).visual;

                if (price != null && Number.isFinite(price)) {
                    if (visual?.visible === false) continue;
                    levels.push({
                        type: 'support',
                        price,
                        touches: touches || 0,
                        strength: strength || 0,
                        index: i,
                        visual,
                    });
                }
            }
            
            // Resistance levels (1-5)
            for (let i = 1; i <= 5; i++) {
                const priceKey = `support_resistance_resistance_${i}`;
                const touchesKey = `support_resistance_resistance_touches_${i}`;
                const strengthKey = `support_resistance_resistance_strength_${i}`;
                
                const priceRaw = data[priceKey];
                const touchesRaw = data[touchesKey];
                const strengthRaw = data[strengthKey];
                
                const price = getLatestValue(priceRaw);
                const touches = getLatestValue(touchesRaw);
                const strength = getLatestValue(strengthRaw);
                const visual = pickSeriesVisual(visualsByKey, indicatorMeta, priceKey).visual;

                if (price != null && Number.isFinite(price)) {
                    if (visual?.visible === false) continue;
                    levels.push({
                        type: 'resistance',
                        price,
                        touches: touches || 0,
                        strength: strength || 0,
                        index: i,
                        visual,
                    });
                }
            }
            
            // Remove existing lines for this indicator
            const existingLines = supportResistanceLinesRef.current.get(indicator.id) || [];
            existingLines.forEach(line => {
                try { mainSeries.removePriceLine(line); } catch { /* ignore */ }
            });
            
            // Create new price lines
            const newLines = [];
            levels.forEach(level => {
                try {
                    const options = buildOptionsFromVisuals(level.visual, {
                        color: level.type === 'support' ? '#22C55E' : '#EF4444',
                        lineWidth: 1,
                        lineStyle: LineStyle.Solid,
                    });

                    const priceLine = mainSeries.createPriceLine({
                        price: level.price,
                        color: options.color,
                        lineWidth: options.lineWidth,
                        lineStyle: options.lineStyle,
                        axisLabelVisible: true,
                        title: `${level.type === 'support' ? 'S' : 'R'}${level.index} (${level.touches})`,
                    });

                    newLines.push(priceLine);
                } catch (err) {
                    console.error(`[SR] Failed to create price line for ${level.type}_${level.index}:`, err);
                }
            });
            supportResistanceLinesRef.current.set(indicator.id, newLines);
        });

    }, [activeIndicators, availableIndicators, isBacktest]);

    // Trade Markers (for backtesting) + optional extra markers (e.g., partial TPs)
    useEffect(() => {
        if (!seriesRef.current || !isBacktest) return;

        if (!markersRef.current) {
            markersRef.current = createSeriesMarkers(seriesRef.current);
        }
        const base = convertMarkers(tradeMarkers);
        const extras = Array.isArray(extraMarkers) ? extraMarkers.filter(m => m && typeof m.time === 'number') : [];
        // Strip price from partial TP marker labels like "TP2 @ 3563.68…" → "TP2"
        const extrasSanitized = extras.map((m) => {
            if (!m || typeof m.text !== 'string') return m;
            const txt = m.text;
            // Heuristic: only adjust labels that look like TP markers (TP, TP1, TP2, etc.)
            const isTp = /^\s*TP\s*\d*/i.test(txt);
            if (!isTp) return m;
            let newText = txt
                // Remove anything starting with '@' to the end
                .replace(/\s*@\s*.*/, '')
                // Or patterns like "TP1 at 1234.56" → remove from " at " to end
                .replace(/\s+at\s+.*/i, '')
                .trim();
            // Avoid empty labels
            if (!newText) newText = txt;
            return { ...m, text: newText };
        });
        const merged = [...base, ...extrasSanitized].sort((a, b) => a.time - b.time);
        try {
            markersRef.current.setMarkers(merged);
        } catch {
            try { markersRef.current.setMarkers(base); } catch { /* ignore */ }
        }
    }, [tradeMarkers, extraMarkers, isBacktest]);

    // Recent Account Trades Markers (for live)
    useEffect(() => {
        if (isBacktest) return; // live only
        if (!seriesRef.current) return;
        if (!showRecentTrades || !selectedAccount) {
            // Clear markers when disabled
            if (!markersRef.current) {
                markersRef.current = createSeriesMarkers(seriesRef.current);
            }
            try { markersRef.current.setMarkers([]); } catch { /* ignore */ }
            recentTradeMarkersRef.current = [];
            return;
        }

        let aborted = false;
        if (!markersRef.current) {
            markersRef.current = createSeriesMarkers(seriesRef.current);
        }

        (async () => {
            try {
                const result = await getAccountTrades(selectedAccount);
                if (aborted) return;
                const trades = Array.isArray(result) ? result : (result?.results || []);
                const m = buildRecentTradeMarkers(trades, selectedSymbol);
                recentTradeMarkersRef.current = m;
                markersRef.current.setMarkers(m);
            } catch (e) {
                console.error('Failed to fetch recent trades', e);
                try { markersRef.current.setMarkers([]); } catch { /* ignore */ }
                recentTradeMarkersRef.current = [];
            }
        })();

        return () => { aborted = true; };
    }, [showRecentTrades, selectedAccount, selectedSymbol, selectedTimeframe, isBacktest]);

    const { paneLabels, overlayLabels } = useMemo(() => {
        const indicators = Array.isArray(activeIndicators) ? activeIndicators : [];
        const paneCounts = Object.create(null);
        const overlayCounts = Object.create(null);

        indicators.forEach((indicator) => {
            const rawName = String(indicator?.name || '').trim();
            if (!rawName || rawName.toLowerCase() === 'price') return;
            const counts = indicator?.paneType === 'pane' ? paneCounts : overlayCounts;
            const key = rawName.toLowerCase();
            counts[key] = (counts[key] || 0) + 1;
        });

        const nextPaneLabels = [];
        const nextOverlayLabels = [];
        let paneIndex = showVolume ? ((volumePaneIndexRef.current ?? 1) + 1) : 1;

        indicators.forEach((indicator) => {
            const rawName = String(indicator?.name || '').trim();
            if (!rawName || rawName.toLowerCase() === 'price') return;
            const key = rawName.toLowerCase();
            if (indicator?.paneType === 'pane') {
                nextPaneLabels.push({
                    paneIndex: paneIndex++,
                    id: indicator.id,
                    name: rawName,
                    text: buildOverlayLabel(rawName, indicator.params, paneCounts[key] > 1),
                });
            } else {
                nextOverlayLabels.push({
                    id: indicator.id,
                    name: rawName,
                    text: buildOverlayLabel(rawName, indicator.params, overlayCounts[key] > 1),
                });
            }
        });

        return { paneLabels: nextPaneLabels, overlayLabels: nextOverlayLabels };
    }, [activeIndicators, showVolume]);

    // Pane and Overlay indicators (Live)
    useEffect(() => {
        if (!chartRef.current || isBacktest) return;

        const currentSeries = indicatorSeriesRef.current;
        const requiredSeriesIds = new Set();

        // Build a stable pane assignment for this render so each pane-type indicator
        // gets its own pane index consistently, regardless of whether it already exists.
    const paneAssignment = new Map();
    // Reserve the volume pane (index 1) when volume is shown, so new pane indicators start after it
    const reservedVolumePane = showVolume ? (volumePaneIndexRef.current ?? 1) : null;
    // Indicators in panes start at index 1 (0 is main price pane), or after reserved volume pane
    let nextPane = (reservedVolumePane != null) ? (reservedVolumePane + 1) : 1;
        activeIndicators.forEach((indicator) => {
            if (indicator?.paneType === 'pane') {
                paneAssignment.set(indicator.id, nextPane++);
            }
        });

        // First, determine all series that should be on the chart
        activeIndicators.forEach(indicator => {
            // Do not render an indicator explicitly named 'Price'
            if (String(indicator?.name || '').trim().toLowerCase() === 'price') return;
            // Skip support_resistance as it's handled with price lines
            if (isSupportResistanceIndicator(indicator)) return;
            if (Array.isArray(indicator.data)) {
                const indicatorMeta = getIndicatorMeta(availableIndicators, indicator);
                const outKeys = getOutputKeys(indicator, indicatorMeta);
                const visualsByKey = (deepMerge(indicatorMeta?.visual_defaults || {}, indicator?.visual_overrides || {})?.series) || {};
                if (outKeys.length > 1) {
                    const allowedSlope = chooseAllowedSlopeKeys(outKeys, indicator.params, visualsByKey);
                    outKeys.forEach((key) => {
                        if (isMarkerVisualOutput(visualsByKey, indicatorMeta, key)) return;
                        if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return;
                        const seriesId = `${indicator.id}-${key}`;
                        requiredSeriesIds.add(seriesId);
                    });
                } else {
                    const key = outKeys[0];
                    if (!isMarkerVisualOutput(visualsByKey, indicatorMeta, key)) {
                        requiredSeriesIds.add(indicator.id);
                    }
                }
            } else if (typeof indicator.data === 'object' && indicator.data) {
                const allKeys = Object.keys(indicator.data);
                const indicatorMeta = getIndicatorMeta(availableIndicators, indicator);
                const visualsByKey = (deepMerge(indicatorMeta?.visual_defaults || {}, indicator?.visual_overrides || {})?.series) || {};
                const allowedSlope = chooseAllowedSlopeKeys(allKeys, indicator.params, visualsByKey);
                allKeys.forEach(key => {
                    if (isMarkerVisualOutput(visualsByKey, indicatorMeta, key)) return;
                    if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return;
                    const seriesId = `${indicator.id}-${key}`;
                    requiredSeriesIds.add(seriesId);
                });
            }
        });

        // Remove old series that are no longer required
        // Important: don't remove panes inline while iterating (pane indices can shift).
        // Collect candidate panes, then remove them in descending index order.
        const candidatePanesForRemoval = new Set();
        currentSeries.forEach((entry, id) => {
            if (!requiredSeriesIds.has(id)) {
                const chart = chartRef.current;
                if (chart) {
                    const paneRef = entry?.pane;
                    try {
                        if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                            try { chart.removeSeries(entry.series.posSeries); } catch { /* ignore */ }
                            try { chart.removeSeries(entry.series.negSeries); } catch { /* ignore */ }
                        } else {
                            chart.removeSeries(entry.series);
                        }
                    } catch { /* ignore */ }
                    if (paneRef) candidatePanesForRemoval.add(paneRef);
                }
                currentSeries.delete(id);
            }
        });

        // After removing series, clean up any now-empty panes by checking tracked usage
        if (candidatePanesForRemoval.size > 0 && chartRef.current) {
            try {
                const usedPaneIdx = new Set();
                currentSeries.forEach((entry) => {
                    const idx = entry?.pane && entry.pane.paneIndex ? entry.pane.paneIndex() : undefined;
                    if (typeof idx === 'number') usedPaneIdx.add(idx);
                });
                const indices = Array.from(candidatePanesForRemoval)
                    .map(p => (p && typeof p.paneIndex === 'function') ? p.paneIndex() : undefined)
                    .filter(i => {
                        if (!(typeof i === 'number' && i > 0)) return false;
                        if (typeof reservedVolumePane === 'number' && i === reservedVolumePane) return false;
                        return !usedPaneIdx.has(i);
                    });
                indices.sort((a, b) => b - a).forEach(idx => {
                    try { chartRef.current.removePane(idx); } catch { /* ignore */ }
                });
            } catch { /* ignore */ }
        }

        // Add or update series
        activeIndicators.forEach(indicator => {
            // Skip drawing the 'Price' indicator altogether
            if (String(indicator?.name || '').trim().toLowerCase() === 'price') return;
            const isPaneIndicator = indicator.paneType === 'pane';
            const defaultPaneStart = (typeof reservedVolumePane === 'number') ? (reservedVolumePane + 1) : 1;
            const currentPaneIndex = isPaneIndicator ? (paneAssignment.get(indicator.id) ?? defaultPaneStart) : 0;
            // Support/resistance uses main-series price lines, but it still
            // participates in the active indicator chip controls above.
            if (isSupportResistanceIndicator(indicator)) return;

            const indicatorMeta = getIndicatorMeta(availableIndicators, indicator);
            const visualDefaults = indicatorMeta?.visual_defaults || {};
            const overrides = indicator?.visual_overrides || {};
            const effectiveVisuals = deepMerge(visualDefaults, overrides);
            const visualsByKey = effectiveVisuals?.series || {};

            const outKeys = getOutputKeys(indicator, indicatorMeta);

            if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
                try {
                    const sample = Array.isArray(indicator.data) ? indicator.data.slice(0, 3) : indicator.data;
                    console.log('[DMI] outKeys:', outKeys, 'paneType:', indicator.paneType, 'sample:', sample);
                } catch { /* ignore */ }
            }

            // Case 0: Multi-output provided as array-of-objects with multiple keys
            if (Array.isArray(indicator.data) && outKeys.length > 1) {
                const allowedSlope = chooseAllowedSlopeKeys(outKeys, indicator.params, visualsByKey);
                outKeys.forEach((key) => {
                    if (isMarkerVisualOutput(visualsByKey, indicatorMeta, key)) return;
                    if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return; // skip unselected slope output
                    const seriesId = `${indicator.id}-${key}`;
                    const normalized = normalizeSeriesPoints(indicator.data, key);
                    const padded = padIndicatorData(historicalData, normalized);

                    const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                    const vForKey = picked.visual;
                    const fallbacks = {
                        color: indicator.params?.[`${key}_color`] || indicator.params?.color || '#FFD700',
                        lineWidth: indicator.params?.[`${key}_width`] || indicator.params?.width || 2,
                        lineStyle: indicator.params?.[`${key}_style`] || indicator.params?.style || LineStyle.Solid || 0,
                    };
                    const options = buildOptionsFromVisuals(vForKey, fallbacks);
                    // For slope outputs, render as a single line series (uniform color)
                    const isSlope = isSlopeOutputKey(key);
                    const plotTypePref = isSlope ? 'line' : (vForKey?.plotType || indicator.params?.[`${key}_plotType`] || indicator.params?.plotType || 'line');
                    const { SeriesCtor, type } = resolveSeriesType(plotTypePref);
                    const typeOpts = toSeriesTypeOptions(type, options);

                    // If this output is slope-like, ensure it gets its own pane
                    const desiredPaneIndex = (isSlope ? (paneAssignment.get(`${indicator.id}::${key}`) ?? (paneAssignment.set(`${indicator.id}::${key}`, nextPane++), paneAssignment.get(`${indicator.id}::${key}`))) : currentPaneIndex);

                    if (!currentSeries.has(seriesId)) {
                        // compute price format for pane indicators to avoid 0.00 for small values
                        const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                        const priceFormat = (isPaneIndicator || isSlope) ? derivePriceFormatFromPoints(pointsForPrecision, digits) : undefined;
                        const seriesOptions = {
                            ...typeOpts,
                            // Hide indicator name from price scale by omitting title
                        };
                        // For overlay indicators on the main pane, hide the dotted current-value line
                        if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                        if (isSlope) seriesOptions.visible = true;
                        if (priceFormat) seriesOptions.priceFormat = priceFormat;
                        const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                        currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                        if (isPaneIndicator || isSlope) { try { newSeries.getPane().setHeight(100); } catch { /* ignore */ } }
                    } else {
                        const entry = currentSeries.get(seriesId);
                        if (entry.plotType !== type) {
                            const pane = desiredPaneIndex;
                            // If prior render left a pair, remove both
                            try {
                                if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                    chartRef.current.removeSeries(entry.series.posSeries);
                                    chartRef.current.removeSeries(entry.series.negSeries);
                                } else {
                                    chartRef.current.removeSeries(entry.series);
                                }
                            } catch { /* ignore */ }
                            const so2 = { ...typeOpts };
                            if (!isPaneIndicator && !isSlope) so2.priceLineVisible = false;
                            if (isSlope) so2.visible = true;
                            const newSeries = chartRef.current.addSeries(SeriesCtor, so2, pane);
                            currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                        } else {
                            const seriesOptions = { ...typeOpts };
                            if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                            if (isPaneIndicator || isSlope) {
                                const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                                seriesOptions.priceFormat = derivePriceFormatFromPoints(pointsForPrecision, digits);
                            }
                            if (isSlope) seriesOptions.visible = true;
                            // If pane changed (e.g., volume pane got reserved), move series to desired pane
                            const actualPane = entry.pane?.paneIndex?.();
                            if ((isPaneIndicator || isSlope) && typeof actualPane === 'number' && actualPane !== desiredPaneIndex) {
                                try {
                                    if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                        chartRef.current.removeSeries(entry.series.posSeries);
                                        chartRef.current.removeSeries(entry.series.negSeries);
                                    } else {
                                        chartRef.current.removeSeries(entry.series);
                                    }
                                } catch { /* ignore */ }
                                const moved = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                                currentSeries.set(seriesId, { series: moved, pane: moved.getPane(), plotType: type });
                            } else {
                                entry.series.applyOptions(seriesOptions);
                            }
                        }
                    }

                    const entryNow = currentSeries.get(seriesId);
                    const hasValues = Array.isArray(padded) && padded.some(pt => Number.isFinite(pt?.value));
                    entryNow.pointVisualOptions = options;
                    setIndicatorSeriesData(entryNow.series, applySeriesPointVisuals(
                        hasValues ? padded : normalized,
                        type,
                        options,
                    ), indicator?.name || 'indicator', type);
                });
                return; // handled this indicator
            }

            // Case 1: Single-value indicator (data is an array)
            if (Array.isArray(indicator.data) && indicator.data.length > 0) {
                const valueKey = outKeys[0] || (indicator.data_keys && indicator.data_keys.length > 0 ? indicator.data_keys[0] : 'value');
                if (isMarkerVisualOutput(visualsByKey, indicatorMeta, valueKey)) return;
                const normalized = normalizeSeriesPoints(indicator.data, valueKey);
                const padded = padIndicatorData(historicalData, normalized);

                const picked = pickSeriesVisual(visualsByKey, indicatorMeta, valueKey);
                const vForKey = picked.visual;
                const fallbacks = {
                    color: indicator.params?.color || '#FFD700',
                    lineWidth: indicator.params?.width || 2,
                    lineStyle: indicator.params?.style || LineStyle.Solid || 0,
                };
                const options = buildOptionsFromVisuals(vForKey, fallbacks);
                const isSlope = isSlopeOutputKey(valueKey);
                const plotTypePref = isSlope ? 'line' : (vForKey?.plotType || indicator.params?.plotType || 'line');
                const { SeriesCtor, type } = resolveSeriesType(plotTypePref);
                const typeOpts = toSeriesTypeOptions(type, options);
                const desiredPaneIndex = (isSlope ? (paneAssignment.get(`${indicator.id}::${valueKey}`) ?? (paneAssignment.set(`${indicator.id}::${valueKey}`, nextPane++), paneAssignment.get(`${indicator.id}::${valueKey}`))) : currentPaneIndex);

                if (!currentSeries.has(indicator.id)) {
                    const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                    const priceFormat = (isPaneIndicator || isSlope) ? derivePriceFormatFromPoints(pointsForPrecision, digits) : undefined;
                    const seriesOptions = { ...typeOpts };
                    if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                    if (priceFormat) seriesOptions.priceFormat = priceFormat;
                    if (isSlope) seriesOptions.visible = true;
                    const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                    currentSeries.set(indicator.id, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    if (isPaneIndicator || isSlope) { try { newSeries.getPane().setHeight(100); } catch { /* ignore */ } }
                } else {
                    const entry = currentSeries.get(indicator.id);
                    if (entry.plotType !== type) {
                        const pane = desiredPaneIndex;
                        try {
                            if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                chartRef.current.removeSeries(entry.series.posSeries);
                                chartRef.current.removeSeries(entry.series.negSeries);
                            } else {
                                chartRef.current.removeSeries(entry.series);
                            }
                        } catch { /* ignore */ }
                        const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                        const seriesOptions = { ...typeOpts };
                        if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                        if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(pointsForPrecision, digits);
                        if (isSlope) seriesOptions.visible = true;
                        const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, pane);
                        currentSeries.set(indicator.id, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    } else {
                        const seriesOptions = { ...typeOpts };
                        if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                        if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(padded, digits);
                        if (isSlope) seriesOptions.visible = true;
                        // If pane changed (e.g., reserve volume pane), move series
                        const actualPane = entry.pane?.paneIndex?.();
                        if ((isPaneIndicator || isSlope) && typeof actualPane === 'number' && actualPane !== desiredPaneIndex) {
                            try {
                                if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                    chartRef.current.removeSeries(entry.series.posSeries);
                                    chartRef.current.removeSeries(entry.series.negSeries);
                                } else {
                                    chartRef.current.removeSeries(entry.series);
                                }
                            } catch { /* ignore */ }
                            const moved = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                            currentSeries.set(indicator.id, { series: moved, pane: moved.getPane(), plotType: type });
                        } else {
                            entry.series.applyOptions(seriesOptions);
                        }
                    }
                }
                const entryNow = currentSeries.get(indicator.id);
                const hasValues1 = Array.isArray(padded) && padded.some(pt => Number.isFinite(pt?.value));
                entryNow.pointVisualOptions = options;
                setIndicatorSeriesData(entryNow.series, applySeriesPointVisuals(
                    hasValues1 ? padded : normalized,
                    type,
                    options,
                ), indicator?.name || 'indicator', type);

            // Case 2: Multi-value indicator (data is an object)
            } else if (typeof indicator.data === 'object' && indicator.data) {
                const keysAll = Object.keys(indicator.data);
                const allowedSlope = chooseAllowedSlopeKeys(keysAll, indicator.params, visualsByKey);
                keysAll.forEach(key => {
                    if (isMarkerVisualOutput(visualsByKey, indicatorMeta, key)) return;
                    if (isSlopeOutputKey(key) && allowedSlope.size > 0 && !allowedSlope.has(key)) return; // skip unselected slope output
                    const seriesId = `${indicator.id}-${key}`;
                    const seriesData = indicator.data[key];

                    if (seriesData && seriesData.length > 0) {
                        const normalized = normalizeSeriesPoints(seriesData, key);
                        if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
                            try { console.log('[DMI][norm]', key, normalized.slice(0, 3)); } catch { /* ignore */ }
                        }
                        const padded = padIndicatorData(historicalData, normalized);

                        const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                        const vForKey = picked.visual;
                        const fallbacks = {
                            color: indicator.params?.[`${key}_color`] || '#FFD700',
                            lineWidth: indicator.params?.[`${key}_width`] || 2,
                            lineStyle: indicator.params?.[`${key}_style`] || LineStyle.Solid || 0,
                        };
                        const options = buildOptionsFromVisuals(vForKey, fallbacks);
                        const isSlope = isSlopeOutputKey(key);
                        const plotTypePref = isSlope ? 'line' : (vForKey?.plotType || indicator.params?.[`${key}_plotType`] || 'line');
                        const { SeriesCtor, type } = resolveSeriesType(plotTypePref);
                        const typeOpts = toSeriesTypeOptions(type, options);
                        const desiredPaneIndex = (isSlope ? (paneAssignment.get(`${indicator.id}::${key}`) ?? (paneAssignment.set(`${indicator.id}::${key}`, nextPane++), paneAssignment.get(`${indicator.id}::${key}`))) : currentPaneIndex);

                        if (!currentSeries.has(seriesId)) {
                            const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                            const priceFormat = (isPaneIndicator || isSlope) ? derivePriceFormatFromPoints(pointsForPrecision, digits) : undefined;
                            const seriesOptions = { ...typeOpts };
                            if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                            if (priceFormat) seriesOptions.priceFormat = priceFormat;
                            if (isSlope) seriesOptions.visible = true;
                            const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                            currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                            if (isPaneIndicator || isSlope) { try { newSeries.getPane().setHeight(100); } catch { /* ignore */ } }
                        } else {
                            const entry = currentSeries.get(seriesId);
                            if (entry.plotType !== type) {
                                const pane = desiredPaneIndex;
                                try {
                                    if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                        chartRef.current.removeSeries(entry.series.posSeries);
                                        chartRef.current.removeSeries(entry.series.negSeries);
                                    } else {
                                        chartRef.current.removeSeries(entry.series);
                                    }
                                } catch { /* ignore */ }
                                const pointsForPrecision = padded && Array.isArray(padded) ? padded : [];
                                const seriesOptions = { ...typeOpts };
                                if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                                if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(pointsForPrecision, digits);
                                if (isSlope) seriesOptions.visible = true;
                                const newSeries = chartRef.current.addSeries(SeriesCtor, seriesOptions, pane);
                                currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                            } else {
                                const seriesOptions = { ...typeOpts };
                                if (!isPaneIndicator && !isSlope) seriesOptions.priceLineVisible = false;
                                if (isPaneIndicator || isSlope) seriesOptions.priceFormat = derivePriceFormatFromPoints(padded, digits);
                                if (isSlope) seriesOptions.visible = true;
                                // If pane changed, move series to desired pane
                                const actualPane = entry.pane?.paneIndex?.();
                                if ((isPaneIndicator || isSlope) && typeof actualPane === 'number' && actualPane !== desiredPaneIndex) {
                                    try {
                                        if (entry.series && entry.series.posSeries && entry.series.negSeries) {
                                            chartRef.current.removeSeries(entry.series.posSeries);
                                            chartRef.current.removeSeries(entry.series.negSeries);
                                        } else {
                                            chartRef.current.removeSeries(entry.series);
                                        }
                                    } catch { /* ignore */ }
                                    const moved = chartRef.current.addSeries(SeriesCtor, seriesOptions, desiredPaneIndex);
                                    currentSeries.set(seriesId, { series: moved, pane: moved.getPane(), plotType: type });
                                } else {
                                    entry.series.applyOptions(seriesOptions);
                                }
                            }
                        }
                        const entryNow = currentSeries.get(seriesId);
                        const hasValues2 = Array.isArray(padded) && padded.some(pt => Number.isFinite(pt?.value));
                        entryNow.pointVisualOptions = options;
                        setIndicatorSeriesData(entryNow.series, applySeriesPointVisuals(
                            hasValues2 ? padded : normalized,
                            type,
                            options,
                        ), indicator?.name || 'indicator', type);
                    }
                });
            }
        });
        schedulePaneTopsRecompute();

    }, [activeIndicators, isBacktest, historicalData, availableIndicators, showVolume, digits, isLoggingEnabled, schedulePaneTopsRecompute]);

    // Render sparse marker outputs as chart markers instead of connecting
    // unrelated occurrences with a line. Confirmed RSI divergence is handled
    // by the structured-event renderer below because it also needs isolated
    // pivot-to-pivot segments.
    useEffect(() => {
        if (!chartRef.current || !seriesRef.current || isBacktest) return;

        const markerEntries = indicatorSparseMarkersRef.current;
        const realToChartTime = new Map();
        (historicalData || []).forEach((bar) => {
            const real = Number(bar?.time_real ?? bar?.time);
            const plotted = Number(bar?.time);
            if (Number.isFinite(real) && Number.isFinite(plotted)) {
                realToChartTime.set(real > 1000000000000 ? Math.floor(real / 1000) : real, plotted);
            }
        });
        const toChartTime = (value) => {
            let seconds = Number(value);
            if (!Number.isFinite(seconds)) return null;
            if (seconds > 1000000000000) seconds = Math.floor(seconds / 1000);
            return realToChartTime.get(seconds) ?? seconds;
        };
        const markerShape = (rawShape, position) => {
            const shape = String(rawShape || '').trim().toLowerCase();
            if (shape === 'dot' || shape === 'circle') return 'circle';
            if (shape === 'square') return 'square';
            if (shape === 'arrow_up' || shape === 'arrowup') return 'arrowUp';
            if (shape === 'arrow_down' || shape === 'arrowdown') return 'arrowDown';
            if (shape === 'inverted_triangle') return 'arrowDown';
            if (shape === 'triangle') return position === 'aboveBar' ? 'arrowDown' : 'arrowUp';
            return position === 'aboveBar' ? 'arrowDown' : 'arrowUp';
        };
        const markerPosition = (visualKey, rawShape) => {
            const key = String(visualKey || '').toLowerCase();
            const shape = String(rawShape || '').toLowerCase();
            if (
                /bear|high|shooting|gravestone|hanging/.test(key)
                || /down|inverted/.test(shape)
            ) return 'aboveBar';
            return 'belowBar';
        };
        const pointsForKey = (indicator, runtimeKey) => {
            if (Array.isArray(indicator?.data)) {
                return normalizeSeriesPoints(indicator.data, runtimeKey);
            }
            if (!indicator?.data || typeof indicator.data !== 'object') return [];
            const storedKey = Object.keys(indicator.data).find((key) => (
                runtimeSeriesKeyMatches(key, runtimeKey)
                || runtimeSeriesKeyMatches(runtimeKey, key)
            ));
            return storedKey
                ? normalizeSeriesPoints(indicator.data[storedKey], storedKey)
                : [];
        };

        const markerIndicatorIds = new Set();
        (activeIndicators || []).forEach((indicator) => {
            const normalizedName = normalizeIndicatorMetaKey(indicator?.name).replace(/[_\s-]/g, '');
            if (normalizedName === 'rsidivergenceconfirmed') return;

            const indicatorMeta = getIndicatorMeta(availableIndicators, indicator);
            const effectiveVisuals = deepMerge(
                indicatorMeta?.visual_defaults || {},
                indicator?.visual_overrides || {},
            );
            const visualsByKey = effectiveVisuals?.series || {};
            const outputKeys = getOutputKeys(indicator, indicatorMeta);
            const markerKeys = outputKeys.filter((key) => (
                isMarkerVisualOutput(visualsByKey, indicatorMeta, key)
            ));
            if (markerKeys.length === 0) return;
            markerIndicatorIds.add(indicator.id);

            const nonMarkerKey = outputKeys.find((key) => (
                !isMarkerVisualOutput(visualsByKey, indicatorMeta, key)
            ));
            const paneAnchor = nonMarkerKey
                ? (
                    indicatorSeriesRef.current.get(`${indicator.id}-${nonMarkerKey}`)
                    || indicatorSeriesRef.current.get(indicator.id)
                )?.series
                : null;
            const anchorSeries = paneAnchor || seriesRef.current;
            if (!anchorSeries) return;

            const markers = [];
            markerKeys.forEach((runtimeKey) => {
                const picked = pickSeriesVisual(visualsByKey, indicatorMeta, runtimeKey);
                const visual = picked.visual || {};
                if (visual.visible === false) return;
                const position = markerPosition(picked.key || runtimeKey, visual.shape);
                pointsForKey(indicator, runtimeKey).forEach((point) => {
                    if (!Number.isFinite(point?.value)) return;
                    const time = toChartTime(point.time);
                    if (!Number.isFinite(time)) return;
                    markers.push({
                        time,
                        position,
                        color: visual.color || '#808080',
                        shape: markerShape(visual.shape, position),
                    });
                });
            });
            markers.sort((a, b) => a.time - b.time);

            let entry = markerEntries.get(indicator.id);
            if (!entry || entry.series !== anchorSeries) {
                detachMarkerPrimitive(entry?.primitive);
                try {
                    entry = {
                        series: anchorSeries,
                        primitive: createSeriesMarkers(anchorSeries, []),
                    };
                    markerEntries.set(indicator.id, entry);
                } catch {
                    entry = null;
                }
            }
            try { entry?.primitive?.setMarkers(markers); } catch { /* ignore */ }
        });

        markerEntries.forEach((entry, indicatorId) => {
            if (markerIndicatorIds.has(indicatorId)) return;
            detachMarkerPrimitive(entry?.primitive);
            markerEntries.delete(indicatorId);
        });
    }, [activeIndicators, availableIndicators, historicalData, isBacktest]);

    // Confirmed RSI-divergence annotations use structured causal events.  The
    // marker is placed on the confirmation bar, while each visual pivot segment
    // receives its own series so unrelated divergences can never be connected.
    useEffect(() => {
        if (!chartRef.current || isBacktest) return;

        const chart = chartRef.current;
        const markerEntries = indicatorEventMarkersRef.current;
        const segmentEntries = indicatorEventSegmentsRef.current;
        const guideEntries = indicatorGuideLinesRef.current;
        const confirmedIndicators = (activeIndicators || []).filter((indicator) => (
            normalizeIndicatorMetaKey(indicator?.name).replace(/[_\s-]/g, '')
            === 'rsidivergenceconfirmed'
        ));
        const activeIds = new Set(confirmedIndicators.map((indicator) => indicator.id));

        const realToChartTime = new Map();
        (historicalData || []).forEach((bar) => {
            const real = Number(bar?.time_real ?? bar?.time);
            const plotted = Number(bar?.time);
            if (Number.isFinite(real) && Number.isFinite(plotted)) {
                realToChartTime.set(real > 1000000000000 ? Math.floor(real / 1000) : real, plotted);
            }
        });
        const toChartTime = (value) => {
            let seconds = Number(value);
            if (!Number.isFinite(seconds)) return null;
            if (seconds > 1000000000000) seconds = Math.floor(seconds / 1000);
            return realToChartTime.get(seconds) ?? seconds;
        };
        const eventVisualKey = (event) => {
            if (event?.direction === 'bullish') return event?.hidden ? 'bull_hidden_div' : 'bull_div';
            return event?.hidden ? 'bear_hidden_div' : 'bear_div';
        };
        const eventMarkerShape = (shape, bullish) => {
            const normalized = String(shape || '').trim().toLowerCase();
            if (normalized === 'circle' || normalized === 'dot') return 'circle';
            if (normalized === 'square') return 'square';
            if (normalized === 'arrow_up' || normalized === 'arrowup') return 'arrowUp';
            if (normalized === 'arrow_down' || normalized === 'arrowdown') return 'arrowDown';
            // lightweight-charts has no triangle marker; use a circle for the
            // legacy alias so hidden divergence remains visually distinct.
            if (normalized === 'triangle') return 'circle';
            return bullish ? 'arrowUp' : 'arrowDown';
        };

        confirmedIndicators.forEach((indicator) => {
            const indicatorMeta = getIndicatorMeta(availableIndicators, indicator);
            const effectiveVisuals = deepMerge(
                indicatorMeta?.visual_defaults || {},
                indicator?.visual_overrides || {},
            );
            const visualsByKey = effectiveVisuals?.series || {};
            const outKeys = getOutputKeys(indicator, indicatorMeta);
            const rsiKey = outKeys.find((key) => runtimeSeriesKeyMatches(key, 'rsi')) || 'rsi';
            const rsiEntry = (
                indicatorSeriesRef.current.get(`${indicator.id}-${rsiKey}`)
                || indicatorSeriesRef.current.get(indicator.id)
            );
            const rsiSeries = rsiEntry?.series;
            if (!rsiSeries) return;

            const events = Array.isArray(indicator?.events)
                ? indicator.events.filter((event) => event && event.id)
                : [];
            const markers = events.map((event) => {
                const time = toChartTime(event.confirmed_time);
                if (!Number.isFinite(time)) return null;
                const key = eventVisualKey(event);
                const visual = pickSeriesVisual(visualsByKey, indicatorMeta, key).visual || {};
                if (visual.visible === false) return null;
                const bullish = event.direction === 'bullish';
                return {
                    time,
                    position: bullish ? 'belowBar' : 'aboveBar',
                    color: visual.color || (bullish ? '#16c784' : '#ef4444'),
                    shape: eventMarkerShape(visual.shape, bullish),
                    text: event.hidden
                        ? (bullish ? 'Hidden bull' : 'Hidden bear')
                        : (bullish ? 'Bull div' : 'Bear div'),
                };
            }).filter(Boolean).sort((a, b) => a.time - b.time);

            let markerEntry = markerEntries.get(indicator.id);
            if (!markerEntry || markerEntry.series !== rsiSeries) {
                detachMarkerPrimitive(markerEntry?.primitive);
                try {
                    markerEntry = {
                        series: rsiSeries,
                        primitive: createSeriesMarkers(rsiSeries, []),
                    };
                    markerEntries.set(indicator.id, markerEntry);
                } catch {
                    markerEntry = null;
                }
            }
            try { markerEntry?.primitive?.setMarkers(markers); } catch { /* ignore */ }

            let perIndicatorSegments = segmentEntries.get(indicator.id);
            if (!perIndicatorSegments) {
                perIndicatorSegments = new Map();
                segmentEntries.set(indicator.id, perIndicatorSegments);
            }
            const expectedSegments = new Set();
            const paneIndex = rsiEntry?.pane?.paneIndex?.() ?? 1;
            events.forEach((event) => {
                const startTime = toChartTime(event.pivot_start_time);
                const endTime = toChartTime(event.pivot_end_time);
                const startValue = Number(event.pivot_start_rsi);
                const endValue = Number(event.pivot_end_rsi);
                if (
                    !Number.isFinite(startTime)
                    || !Number.isFinite(endTime)
                    || !Number.isFinite(startValue)
                    || !Number.isFinite(endValue)
                ) return;

                const key = eventVisualKey(event);
                const lineKey = `${key}_line`;
                const markerVisual = pickSeriesVisual(visualsByKey, indicatorMeta, key).visual || {};
                const lineVisual = pickSeriesVisual(visualsByKey, indicatorMeta, lineKey).visual || markerVisual;
                if (lineVisual.visible === false) return;
                const seriesOptions = {
                    color: lineVisual.color || markerVisual.color || (event.direction === 'bullish' ? '#16c784' : '#ef4444'),
                    lineWidth: Number(lineVisual.lineWidth) || 1,
                    lineStyle: lineStyleMap[lineVisual.lineStyle] ?? (event.hidden ? LineStyle.Dashed : LineStyle.Solid),
                    priceLineVisible: false,
                    lastValueVisible: false,
                };
                const segmentId = String(event.id);
                expectedSegments.add(segmentId);
                let segment = perIndicatorSegments.get(segmentId);
                if (!segment) {
                    try {
                        segment = chart.addSeries(LineSeries, seriesOptions, paneIndex);
                        perIndicatorSegments.set(segmentId, segment);
                    } catch {
                        segment = null;
                    }
                } else {
                    try { segment.applyOptions(seriesOptions); } catch { /* ignore */ }
                }
                try {
                    segment?.setData([
                        { time: startTime, value: startValue },
                        { time: endTime, value: endValue },
                    ]);
                } catch { /* ignore */ }
            });
            perIndicatorSegments.forEach((segment, eventId) => {
                if (expectedSegments.has(eventId)) return;
                try { chart.removeSeries(segment); } catch { /* ignore */ }
                perIndicatorSegments.delete(eventId);
            });

            const guideConfig = effectiveVisuals?.guides || {};
            let guideEntry = guideEntries.get(indicator.id);
            if (!guideEntry || guideEntry.series !== rsiSeries) {
                if (guideEntry) {
                    guideEntry.lines.forEach((line) => {
                        try { guideEntry.series.removePriceLine(line); } catch { /* ignore */ }
                    });
                }
                guideEntry = { series: rsiSeries, lines: [] };
                guideEntries.set(indicator.id, guideEntry);
            } else {
                guideEntry.lines.forEach((line) => {
                    try { rsiSeries.removePriceLine(line); } catch { /* ignore */ }
                });
                guideEntry.lines = [];
            }
            Object.entries(guideConfig).forEach(([name, guide]) => {
                const price = Number(guide?.value);
                if (guide?.visible === false || !Number.isFinite(price)) return;
                try {
                    const line = rsiSeries.createPriceLine({
                        price,
                        color: guide?.color || '#808080',
                        lineWidth: Number(guide?.lineWidth) || 1,
                        lineStyle: lineStyleMap[guide?.lineStyle] ?? LineStyle.Dashed,
                        axisLabelVisible: false,
                        title: String(name || ''),
                    });
                    guideEntry.lines.push(line);
                } catch { /* ignore */ }
            });
        });

        markerEntries.forEach((entry, indicatorId) => {
            if (activeIds.has(indicatorId)) return;
            detachMarkerPrimitive(entry?.primitive);
            markerEntries.delete(indicatorId);
        });
        segmentEntries.forEach((segments, indicatorId) => {
            if (activeIds.has(indicatorId)) return;
            segments.forEach((series) => {
                try { chart.removeSeries(series); } catch { /* ignore */ }
            });
            segmentEntries.delete(indicatorId);
        });
        guideEntries.forEach((entry, indicatorId) => {
            if (activeIds.has(indicatorId)) return;
            entry.lines.forEach((line) => {
                try { entry.series.removePriceLine(line); } catch { /* ignore */ }
            });
            guideEntries.delete(indicatorId);
        });
    }, [activeIndicators, availableIndicators, historicalData, isBacktest]);

    // Backtest Indicators
    useEffect(() => {
        if (!chartRef.current || !isBacktest) return;

        const chart = chartRef.current;
        const currentBacktestSeries = backtestIndicatorLineRefs.current;
        const markerEntries = backtestIndicatorMarkersRef.current;
        const segmentEntries = backtestIndicatorSegmentsRef.current;
        const guideEntries = backtestIndicatorGuidesRef.current;
        let colorIndex = 0;
        const isPriceKey = (key) => {
            const b = key ? key.split('_')[0] : '';
            return String(b).toLowerCase() === 'price';
        };
        const baseNameOf = (key) => {
            if (!key) return '';
            const idx = key.indexOf('_');
            return idx === -1 ? key : key.slice(0, idx);
        };
        const parseParentFromKey = (key) => {
            if (!key) return null;
            const token = '_src_';
            const idx = key.lastIndexOf(token);
            if (idx !== -1) {
                const parent = key.slice(idx + token.length);
                return parent ? parent.split('_')[0] : null;
            }
            return null;
        };

        const allKeys = Object.keys(backtestIndicatorData || {});
        const baseToKeys = new Map();
        const metadataByKey = new Map();
        const visualsByGroup = new Map();
        for (const k of allKeys) {
            const b = baseNameOf(k);
            if (!baseToKeys.has(b)) baseToKeys.set(b, []);
            baseToKeys.get(b).push(k);
            const group = backtestIndicatorData[k];
            const metadata = getBacktestIndicatorMeta(availableIndicators, k, group);
            metadataByKey.set(k, metadata);
            visualsByGroup.set(
                k,
                deepMerge(
                    metadata?.visual_defaults || {},
                    group?.visual_overrides || group?.meta?.visual_overrides || {},
                ),
            );
        }

        const isVolumeOverlayGroup = (key, group) => {
            if (!group || !group.outputs) return false;
            if (group.meta && group.meta.volume_overlay === true) return true;
            if (/_source_volume/i.test(key)) return true;
            return false;
        };
        const pickPrimaryVolumeMAOutput = (group) => {
            if (!group?.outputs) return null;
            const preferred = ['ema','sma','wma','rma'];
            for (const name of preferred) {
                if (group.outputs[name] && Array.isArray(group.outputs[name]) && group.outputs[name].length > 0) return { name, points: group.outputs[name] };
            }
            return null;
        };

        const paneAssignment = new Map();
        if (showVolume && volumePaneIndexRef.current == null) {
            volumePaneIndexRef.current = 1;
        }
        const reservedVolumePane = showVolume ? volumePaneIndexRef.current : null;
        let nextPaneIndex = (reservedVolumePane != null) ? reservedVolumePane + 1 : 1;
        for (const k of allKeys) {
            const group = backtestIndicatorData[k];
            if (isPriceKey(k)) continue;
            if (String(group?.pane_type || '').toLowerCase() === 'pane') {
                if (!paneAssignment.has(k)) {
                    paneAssignment.set(k, nextPaneIndex++);
                }
            }
        }

        const newSeriesIds = new Set();
        for (const k of allKeys) {
            const group = backtestIndicatorData[k];
            if (isPriceKey(k)) continue;
            if (!group?.outputs) continue;
            const metadata = metadataByKey.get(k);
            const visuals = visualsByGroup.get(k)?.series || {};
            for (const outName of Object.keys(group.outputs)) {
                const visual = pickSeriesVisual(visuals, metadata, outName).visual;
                if (visual?.visible === false) continue;
                if (isMarkerVisualOutput(visuals, metadata, outName)) continue;
                if (normalizeBacktestIndicatorPoints(group.outputs[outName]).length === 0) continue;
                newSeriesIds.add(`${k}-${outName}`);
            }
        }

        currentBacktestSeries.forEach((series, seriesId) => {
            if (!newSeriesIds.has(seriesId)) {
                try {
                    const pane = series.getPane && series.getPane();
                    chart.removeSeries(series);
                    if (pane && typeof pane.getSeries === 'function' && pane.paneIndex && pane.paneIndex() > 0) {
                        if ((pane.getSeries()?.length || 0) === 0) {
                            try { chart.removePane(pane.paneIndex()); } catch { /* ignore */ }
                        }
                    }
                } catch { /* ignore */ }
                currentBacktestSeries.delete(seriesId);
                backtestSeriesKindRef.current.delete(seriesId);
            }
        });

        for (const indicatorKey of allKeys) {
            const indicatorGroup = backtestIndicatorData[indicatorKey];
            if (isPriceKey(indicatorKey)) continue;
            if (!indicatorGroup?.outputs) continue;
            if (isVolumeOverlayGroup(indicatorKey, indicatorGroup)) continue;

            const metadata = metadataByKey.get(indicatorKey);
            const effectiveVisuals = visualsByGroup.get(indicatorKey) || {};
            const visuals = effectiveVisuals.series || {};
            const isPaneIndicator = String(indicatorGroup.pane_type || '').toLowerCase() === 'pane';
            const parentBase = parseParentFromKey(indicatorKey);
            let targetPane = 0;
            if (isPaneIndicator && paneAssignment.has(indicatorKey)) {
                targetPane = paneAssignment.get(indicatorKey) ?? 0;
            } else if (parentBase) {
                const parentKeys = baseToKeys.get(parentBase) || [];
                const parentKeyWithPane = parentKeys.find(pk => paneAssignment.has(pk));
                if (parentKeyWithPane) targetPane = paneAssignment.get(parentKeyWithPane) ?? 0;
            } else if (isPaneIndicator) {
                targetPane = paneAssignment.get(indicatorKey) ?? 0;
            }

            for (const outputName of Object.keys(indicatorGroup.outputs)) {
                const rawPoints = indicatorGroup.outputs[outputName];
                const seriesId = `${indicatorKey}-${outputName}`;
                if (!rawPoints) continue;
                const pickedVisual = pickSeriesVisual(visuals, metadata, outputName);
                const visual = pickedVisual.visual || {};
                if (visual.visible === false) continue;
                if (isMarkerVisualOutput(visuals, metadata, outputName)) continue;
                const dedupedData = normalizeBacktestIndicatorPoints(rawPoints);
                if (dedupedData.length === 0) continue;

                const slopeLike = isSlopeOutputKey(outputName);
                let finalPane = targetPane;
                if (slopeLike) {
                    const key = `${indicatorKey}::${outputName}`;
                    if (!paneAssignment.has(key)) {
                        paneAssignment.set(key, nextPaneIndex++);
                    }
                    finalPane = paneAssignment.get(key) ?? targetPane;
                }

                const requestedPlotType = slopeLike
                    ? 'line'
                    : (visual.plotType || (/hist/i.test(outputName) ? 'histogram' : 'line'));
                const { SeriesCtor, type: seriesType } = resolveSeriesType(requestedPlotType);
                const visualOptions = buildOptionsFromVisuals(visual, {
                    color: indicatorColors[colorIndex % indicatorColors.length],
                    lineWidth: 1,
                });
                const seriesOptions = toSeriesTypeOptions(seriesType, visualOptions);
                if (!(isPaneIndicator || finalPane > 0)) {
                    seriesOptions.priceLineVisible = false;
                }
                if (slopeLike || isPaneIndicator || finalPane > 0) {
                    seriesOptions.priceFormat = derivePriceFormatFromPoints(dedupedData, digits);
                }
                const renderedData = applySeriesPointVisuals(
                    dedupedData,
                    seriesType,
                    visualOptions,
                );

                if (!currentBacktestSeries.has(seriesId)) {
                    const newSeries = chart.addSeries(SeriesCtor, seriesOptions, finalPane);
                    if ((slopeLike || isPaneIndicator || finalPane > 0) && chart && typeof newSeries.getPane === 'function') {
                        try { newSeries.getPane().setHeight(100); } catch { /* ignore */ }
                    }
                    currentBacktestSeries.set(seriesId, newSeries);
                    backtestSeriesKindRef.current.set(seriesId, seriesType);
                    setIndicatorSeriesData(newSeries, renderedData, `${indicatorKey}:${outputName}`, seriesType);
                } else {
                    const existing = currentBacktestSeries.get(seriesId);
                    const existingPane = existing?.getPane && existing.getPane();
                    const existingIdx = existingPane && typeof existingPane.paneIndex === 'function' ? existingPane.paneIndex() : 0;
                    const existingKind = backtestSeriesKindRef.current.get(seriesId);
                    if (existingIdx !== finalPane || existingKind !== seriesType) {
                        try { chart.removeSeries(existing); } catch { /* ignore */ }
                        const newSeries = chart.addSeries(SeriesCtor, seriesOptions, finalPane);
                        if ((slopeLike || isPaneIndicator || finalPane > 0) && chart && typeof newSeries.getPane === 'function') {
                            try { newSeries.getPane().setHeight(100); } catch { /* ignore */ }
                        }
                        currentBacktestSeries.set(seriesId, newSeries);
                        backtestSeriesKindRef.current.set(seriesId, seriesType);
                        setIndicatorSeriesData(newSeries, renderedData, `${indicatorKey}:${outputName}`, seriesType);
                    } else {
                        try { existing.applyOptions(seriesOptions); } catch { /* ignore */ }
                        setIndicatorSeriesData(existing, renderedData, `${indicatorKey}:${outputName}`, seriesType);
                    }
                }
                colorIndex++;
            }
        }

        const primarySeriesForGroup = (indicatorKey, group) => {
            const metadata = metadataByKey.get(indicatorKey);
            const visuals = visualsByGroup.get(indicatorKey)?.series || {};
            const outputName = Object.keys(group?.outputs || {}).find((candidate) => {
                const visual = pickSeriesVisual(visuals, metadata, candidate).visual;
                return visual?.visible !== false
                    && !isMarkerVisualOutput(visuals, metadata, candidate)
                    && currentBacktestSeries.has(`${indicatorKey}-${candidate}`);
            });
            return outputName
                ? currentBacktestSeries.get(`${indicatorKey}-${outputName}`)
                : null;
        };

        const activeMarkerGroups = new Set();
        const activeSegmentGroups = new Set();
        const activeGuideGroups = new Set();
        for (const indicatorKey of allKeys) {
            const group = backtestIndicatorData[indicatorKey];
            if (isPriceKey(indicatorKey) || !group?.outputs) continue;
            const metadata = metadataByKey.get(indicatorKey);
            const effectiveVisuals = visualsByGroup.get(indicatorKey) || {};
            const visuals = effectiveVisuals.series || {};
            const isPaneIndicator = String(group.pane_type || '').toLowerCase() === 'pane';
            const primarySeries = primarySeriesForGroup(indicatorKey, group);
            const anchorSeries = primarySeries || (!isPaneIndicator ? seriesRef.current : null);
            const events = Array.isArray(group.events) ? group.events.filter(event => event?.id) : [];
            const markerOutputNames = Object.keys(group.outputs).filter((outputName) => (
                isMarkerVisualOutput(visuals, metadata, outputName)
            ));

            if (anchorSeries && (events.length > 0 || markerOutputNames.length > 0)) {
                activeMarkerGroups.add(indicatorKey);
                const markers = [];
                if (events.length > 0) {
                    events.forEach((event) => {
                        const visualKey = divergenceEventVisualKey(event);
                        const visual = pickSeriesVisual(visuals, metadata, visualKey).visual || {};
                        if (visual.visible === false) return;
                        const time = normalizeBacktestEventTime(event.confirmed_time);
                        if (!Number.isFinite(time)) return;
                        const position = backtestMarkerPosition(visualKey, visual.shape, event.direction);
                        markers.push({
                            time,
                            position,
                            color: visual.color || (event.direction === 'bullish' ? '#16c784' : '#ef4444'),
                            shape: backtestMarkerShape(visual.shape, position),
                            text: event.hidden
                                ? (event.direction === 'bullish' ? 'Hidden bull' : 'Hidden bear')
                                : (event.direction === 'bullish' ? 'Bull div' : 'Bear div'),
                        });
                    });
                } else {
                    markerOutputNames.forEach((outputName) => {
                        const picked = pickSeriesVisual(visuals, metadata, outputName);
                        const visual = picked.visual || {};
                        if (visual.visible === false) return;
                        const position = backtestMarkerPosition(picked.key || outputName, visual.shape);
                        normalizeBacktestIndicatorPoints(group.outputs[outputName]).forEach((point) => {
                            if (!Number.isFinite(point.value)) return;
                            markers.push({
                                time: point.time,
                                position,
                                color: visual.color || '#808080',
                                shape: backtestMarkerShape(visual.shape, position),
                            });
                        });
                    });
                }
                markers.sort((a, b) => a.time - b.time);

                let markerEntry = markerEntries.get(indicatorKey);
                if (!markerEntry || markerEntry.series !== anchorSeries) {
                    detachMarkerPrimitive(markerEntry?.primitive);
                    try {
                        markerEntry = {
                            series: anchorSeries,
                            primitive: createSeriesMarkers(anchorSeries, []),
                        };
                        markerEntries.set(indicatorKey, markerEntry);
                    } catch {
                        markerEntry = null;
                    }
                }
                try { markerEntry?.primitive?.setMarkers(markers); } catch { /* ignore */ }
            }

            if (primarySeries && events.length > 0) {
                activeSegmentGroups.add(indicatorKey);
                let perGroupSegments = segmentEntries.get(indicatorKey);
                if (!perGroupSegments) {
                    perGroupSegments = new Map();
                    segmentEntries.set(indicatorKey, perGroupSegments);
                }
                const expectedSegments = new Set();
                const paneIndex = primarySeries.getPane?.().paneIndex?.() ?? paneAssignment.get(indicatorKey) ?? 1;
                events.forEach((event) => {
                    const startTime = normalizeBacktestEventTime(event.pivot_start_time);
                    const endTime = normalizeBacktestEventTime(event.pivot_end_time);
                    const startValue = Number(event.pivot_start_rsi);
                    const endValue = Number(event.pivot_end_rsi);
                    if (
                        !Number.isFinite(startTime)
                        || !Number.isFinite(endTime)
                        || !Number.isFinite(startValue)
                        || !Number.isFinite(endValue)
                    ) return;
                    const visualKey = divergenceEventVisualKey(event);
                    const markerVisual = pickSeriesVisual(visuals, metadata, visualKey).visual || {};
                    const lineVisual = pickSeriesVisual(visuals, metadata, `${visualKey}_line`).visual || markerVisual;
                    if (lineVisual.visible === false) return;
                    const segmentId = String(event.id);
                    expectedSegments.add(segmentId);
                    const segmentOptions = {
                        color: lineVisual.color || markerVisual.color || (event.direction === 'bullish' ? '#16c784' : '#ef4444'),
                        lineWidth: Number(lineVisual.lineWidth) || 1,
                        lineStyle: lineStyleMap[lineVisual.lineStyle] ?? (event.hidden ? LineStyle.Dashed : LineStyle.Solid),
                        priceLineVisible: false,
                        lastValueVisible: false,
                    };
                    let segment = perGroupSegments.get(segmentId);
                    const segmentPane = segment?.getPane?.().paneIndex?.();
                    if (segment && Number.isFinite(segmentPane) && segmentPane !== paneIndex) {
                        try { chart.removeSeries(segment); } catch { /* ignore */ }
                        perGroupSegments.delete(segmentId);
                        segment = null;
                    }
                    if (!segment) {
                        try {
                            segment = chart.addSeries(LineSeries, segmentOptions, paneIndex);
                            perGroupSegments.set(segmentId, segment);
                        } catch {
                            segment = null;
                        }
                    } else {
                        try { segment.applyOptions(segmentOptions); } catch { /* ignore */ }
                    }
                    try {
                        segment?.setData([
                            { time: startTime, value: startValue },
                            { time: endTime, value: endValue },
                        ]);
                    } catch { /* ignore */ }
                });
                perGroupSegments.forEach((segment, eventId) => {
                    if (expectedSegments.has(eventId)) return;
                    try { chart.removeSeries(segment); } catch { /* ignore */ }
                    perGroupSegments.delete(eventId);
                });
            }

            const guideConfig = effectiveVisuals.guides || {};
            if (primarySeries && Object.keys(guideConfig).length > 0) {
                activeGuideGroups.add(indicatorKey);
                let guideEntry = guideEntries.get(indicatorKey);
                if (!guideEntry || guideEntry.series !== primarySeries) {
                    if (guideEntry) {
                        guideEntry.lines.forEach((line) => {
                            try { guideEntry.series.removePriceLine(line); } catch { /* ignore */ }
                        });
                    }
                    guideEntry = { series: primarySeries, lines: [] };
                    guideEntries.set(indicatorKey, guideEntry);
                } else {
                    guideEntry.lines.forEach((line) => {
                        try { primarySeries.removePriceLine(line); } catch { /* ignore */ }
                    });
                    guideEntry.lines = [];
                }
                Object.entries(guideConfig).forEach(([name, guide]) => {
                    const price = Number(guide?.value);
                    if (guide?.visible === false || !Number.isFinite(price)) return;
                    try {
                        guideEntry.lines.push(primarySeries.createPriceLine({
                            price,
                            color: guide?.color || '#808080',
                            lineWidth: Number(guide?.lineWidth) || 1,
                            lineStyle: lineStyleMap[guide?.lineStyle] ?? LineStyle.Dashed,
                            axisLabelVisible: false,
                            title: String(name || ''),
                        }));
                    } catch { /* ignore */ }
                });
            }
        }

        markerEntries.forEach((entry, indicatorKey) => {
            if (activeMarkerGroups.has(indicatorKey)) return;
            detachMarkerPrimitive(entry?.primitive);
            markerEntries.delete(indicatorKey);
        });
        segmentEntries.forEach((segments, indicatorKey) => {
            if (activeSegmentGroups.has(indicatorKey)) return;
            segments.forEach((series) => {
                try { chart.removeSeries(series); } catch { /* ignore */ }
            });
            segmentEntries.delete(indicatorKey);
        });
        guideEntries.forEach((entry, indicatorKey) => {
            if (activeGuideGroups.has(indicatorKey)) return;
            entry.lines.forEach((line) => {
                try { entry.series.removePriceLine(line); } catch { /* ignore */ }
            });
            guideEntries.delete(indicatorKey);
        });

        if (isBacktest && showVolume) {
            ensureVolumeSeries();
            const volPaneIndex = volumePaneIndexRef.current;
            const createdIds = new Set();
            const overlayLabels = [];
            allKeys.forEach(key => {
                const group = backtestIndicatorData[key];
                if (!isVolumeOverlayGroup(key, group)) return;
                const primary = pickPrimaryVolumeMAOutput(group);
                if (!primary) return;
                const seriesId = `__volma__${key}__${primary.name}`;
                const legacyId = `${key}-${primary.name}`;
                if (currentBacktestSeries.has(legacyId)) {
                    try { chart.removeSeries(currentBacktestSeries.get(legacyId)); } catch { /* ignore */ }
                    currentBacktestSeries.delete(legacyId);
                }
                createdIds.add(seriesId);
                const formatted = primary.points
                    .filter(p => p && typeof p.time === 'number' && Number.isFinite(p.value))
                    .map(p => ({ time: p.time > 1000000000000 ? Math.floor(p.time/1000) : p.time, value: p.value }))
                    .sort((a,b)=>a.time-b.time);
                if (formatted.length === 0) return;
                const metadata = metadataByKey.get(key);
                const visuals = visualsByGroup.get(key)?.series || {};
                const visual = pickSeriesVisual(visuals, metadata, primary.name).visual || {};
                const visualOptions = buildOptionsFromVisuals(visual, {
                    color: indicatorColors[volumeOverlaySeriesRef.current.size % indicatorColors.length],
                    lineWidth: 2,
                });
                const seriesOptions = {
                    ...toSeriesTypeOptions('line', visualOptions),
                    priceScaleId: 'volume',
                    priceLineVisible: false,
                };
                if (!volumeOverlaySeriesRef.current.has(seriesId)) {
                    const s = chart.addSeries(LineSeries, seriesOptions, volPaneIndex ?? 0);
                    volumeOverlaySeriesRef.current.set(seriesId, s);
                }
                const sref = volumeOverlaySeriesRef.current.get(seriesId);
                try { sref.applyOptions(seriesOptions); } catch { /* ignore */ }
                try { sref.setData(deduplicateData(formatted)); } catch { /* ignore */ }
                const tokens = key.split('_');
                let lengthToken = tokens[tokens.indexOf('length')+1];
                if (!Number.isFinite(Number(lengthToken))) lengthToken = undefined;
                const base = tokens[0] ? tokens[0].toUpperCase() : primary.name.toUpperCase();
                const label = `${base}${lengthToken ? `(${lengthToken})` : ''} Vol`;
                overlayLabels.push({ id: seriesId, text: label, color: sref.options().color });
            });
            volumeOverlaySeriesRef.current.forEach((series, id) => {
                if (!createdIds.has(id)) {
                    try { chart.removeSeries(series); } catch { /* ignore */ }
                    volumeOverlaySeriesRef.current.delete(id);
                }
            });
            setBacktestVolumeOverlayLabels(overlayLabels);
        } else {
            if (volumeOverlaySeriesRef.current.size) {
                volumeOverlaySeriesRef.current.forEach((s) => { try { chart.removeSeries(s); } catch { /* ignore */ } });
                volumeOverlaySeriesRef.current.clear();
                setBacktestVolumeOverlayLabels([]);
            }
        }
    }, [backtestIndicatorData, isBacktest, showVolume, ensureVolumeSeries, availableIndicators, digits]);


    // Indicator actions (only for live trading)
    const handleAddIndicator = (indicator, params, paneType, scaleType) => {
        if (isBacktest) return; // Prevent adding indicators in backtest mode
        const key = getIndicatorKey(indicator, params, selectedSymbol, selectedTimeframe, paneType);
        sendIndicatorAction('add_indicator', { 
            indicator, 
            params, 
            unique_id: key, 
            count: 2700, // Or a suitable number
            pane_type: paneType, 
            scale_type: scaleType 
        });
    };

    const handleRemoveIndicator = (indicatorId) => {
        if (isBacktest) return; // Prevent removing indicators in backtest mode
        const indicatorToRemove = activeIndicators.find(ind => ind.id === indicatorId);
        if (!indicatorToRemove) return;
        if (typeof removeActiveIndicator === 'function') {
            removeActiveIndicator(indicatorId);
            return;
        }

        setActiveIndicators(activeIndicators.filter((indicator) => indicator.id !== indicatorId));
        if (indicatorToRemove.backendUniqueId) {
            sendIndicatorAction('remove_indicator', { unique_id: indicatorToRemove.backendUniqueId });
        }
    };

    // Indicator meta helpers
    const findMetaByName = (name) => {
        if (!name) return null;
        const lower = String(name).toLowerCase();
        return (availableIndicators && availableIndicators[lower]) || null;
    };
    // Helper to resolve a canonical id for an indicator name (fallback lower-cased name)
    const getIdByName = (name) => {
        const meta = findMetaByName(name);
        return meta?.id || meta?.name || (name ? String(name) : '').toLowerCase();
    };

    const handleSaveProfile = async (profileName) => {
        // Build IndicatorSpec list (backend-expected shape)
        const indicatorsPayload = [];
        // 1) Base indicators: name + PRICE input
        activeIndicators.forEach((i) => {
            if (!i?.name) return;
            indicatorsPayload.push({
                name: i.name,
                params: i.params || {},
                inputs: [ { ref: 'PRICE', params: { field: 'close' } } ],
            });
        });
        // 2) Nested spec overlays: child with input = parent spec
        try {
            if (specStreams && typeof specStreams === 'object') {
                Object.entries(specStreams).forEach(([uid, entry]) => {
                    if (!uid || (!entry?.attachToId && !entry?.attachToMeta)) return;
                    // Try to derive names/params from UID; fall back to attach meta
                    let childName, childParams = {}, parentName, parentParams = {}, output;
                    try {
                        const parts = String(uid).split(':');
                        if (parts.length >= 7 && parts[0] === 'spec' && parts[2] === 'on') {
                            childName = parts[1];
                            const parentId = parts[3];
                            output = parts[4] && parts[4] !== 'value' ? parts[4] : undefined;
                            const tail = parts.slice(5).join(':');
                            const pivot = tail.lastIndexOf('}:');
                            if (pivot !== -1) {
                                const a = tail.slice(0, pivot + 1);
                                const b = tail.slice(pivot + 2);
                                childParams = JSON.parse(a);
                                parentParams = JSON.parse(b);
                            }
                            // Prefer human name if we have it
                            parentName = entry?.attachToMeta?.parentName || parentId;
                        }
                    } catch { /* ignore */ }
                    if (!parentName) parentName = entry?.attachToMeta?.parentName;
                    if (!parentParams) parentParams = entry?.attachToMeta?.parentParams || {};
                    if (!childName) childName = undefined; // unknown child; skip if missing
                    if (!parentName || !childName) return;

                    // Use provided names directly (backend does not validate here)
                    const parentNode = {
                        name: parentName,
                        params: parentParams || {},
                        inputs: [ { ref: 'PRICE', params: { field: 'close' } } ],
                        ...(output ? { output } : {}),
                    };
                    indicatorsPayload.push({
                        name: childName,
                        params: childParams || {},
                        inputs: [ parentNode ],
                    });
                });
            }
        } catch (e) {
            console.warn('Failed to serialize nested specs for profile:', e);
        }
        try {
            const newProfile = await createChartProfile(
                profileName,
                selectedSymbol,
                selectedTimeframe,
                indicatorsPayload
            );
            setChartProfiles((prev) => [...normalizeChartProfiles(prev), newProfile]);
            setSelectedProfile(newProfile.id);
            alert('Profile saved!');
        } catch (error) {
            console.error('Failed to save profile:', error);
            alert('Failed to save profile.');
        }
    };

    const handleLoadProfile = (profileId) => {
        const profile = normalizeChartProfiles(chartProfiles).find(p => p.id === profileId);
        if (!profile) return;

        // Clear existing indicators before loading new ones
        activeIndicators.forEach(ind => {
            if (ind.backendUniqueId) {
                handleRemoveIndicator(ind.id);
            }
        });

        // Helpers to add base indicators and restore nested specs
        const addedBaseKeys = new Set();
        const ensureBaseFromNode = (node) => {
            if (!node || !node.name) return;
            const meta = findMetaByName(node.name);
            const pane = meta?.pane_type || 'overlay';
            const scale = meta?.scale_type;
            const key = getIndicatorKey(node.name, node.params || {}, selectedSymbol, selectedTimeframe, pane);
            if (addedBaseKeys.has(key)) return;
            handleAddIndicator(node.name, node.params || {}, pane, scale);
            addedBaseKeys.add(key);
        };
        const recurseEnsureChain = (node) => {
            if (!node || !node.inputs || !Array.isArray(node.inputs) || node.inputs.length === 0) return;
            const first = node.inputs[0];
            if (first.ref === 'PRICE') return;
            if (first.name) {
                recurseEnsureChain(first);
                ensureBaseFromNode(first);
            }
        };

        const arr = Array.isArray(profile.indicators) ? profile.indicators : [];
        arr.forEach((spec) => {
            if (!spec || typeof spec !== 'object') return;
            const hasNested = Array.isArray(spec.inputs) && spec.inputs.length > 0 && !!spec.inputs[0] && !spec.inputs[0].ref;
            if (!hasNested) {
                // Base indicator: name + PRICE input
                ensureBaseFromNode(spec);
                return;
            }
            // Nested: ensure parent chain exists, then subscribe overlay attached to direct parent
            const parent = spec.inputs[0];
            recurseEnsureChain(spec); // ensure all upstream parents are added
            ensureBaseFromNode(parent);
            const parentMeta = findMetaByName(parent.name);
            const pane = parentMeta?.pane_type || 'overlay';
            const attachToId = getIndicatorKey(parent.name, parent.params || {}, selectedSymbol, selectedTimeframe, pane);
            // Build spec for subscribeSpec exactly as saved
            const parentNode = { ...parent };
            // Normalize leaf PRICE input if missing params
            try {
                if (Array.isArray(parentNode.inputs) && parentNode.inputs[0]?.ref === 'PRICE' && !parentNode.inputs[0].params) {
                    parentNode.inputs[0] = { ref: 'PRICE', params: { field: 'close' } };
                }
            } catch { /* ignore */ }
            const childName = spec.name;
            const childParams = spec.params || {};
            const childId = getIdByName(childName);
            const parentId = getIdByName(parent.name);
            const out = parent.output;
            const uid = `spec:${childId}:on:${parentId}:${out || 'value'}:${JSON.stringify(childParams)}:${JSON.stringify(parent.params || {})}`;
            const overlaySpec = { name: childId, params: childParams, inputs: [ parentNode ] };
            const attachToMeta = { parentId: attachToId, parentName: parent.name, parentParams: parent.params || {} };
            subscribeSpec && subscribeSpec(uid, overlaySpec, { timeframe: selectedTimeframe, count: 2500, attachToId, attachToMeta });
        });
        setSelectedProfile(profileId);
    };

    const handleDeleteProfile = async (profileId) => {
        try {
            await deleteChartProfile(profileId);
            setChartProfiles((prev) => normalizeChartProfiles(prev).filter(p => p.id !== profileId));
            if (selectedProfile === profileId) {
                setSelectedProfile('');
            }
            alert('Profile deleted.');
        } catch (error) {
            console.error('Failed to delete profile:', error);
            alert('Failed to delete profile.');
        }
    };

    // Helper to immediately apply visuals for one indicator instance
    const applyVisualsForIndicator = (indicator) => {
        if (!chartRef.current || !indicator) return;
        const currentSeries = indicatorSeriesRef.current;
        const isPaneIndicator = indicator.paneType === 'pane';

        const indicatorMeta = getIndicatorMeta(availableIndicators, indicator);
        const visualDefaults = indicatorMeta?.visual_defaults || {};
        const overrides = indicator?.visual_overrides || {};
        const effectiveVisuals = deepMerge(visualDefaults, overrides);
        const visualsByKey = effectiveVisuals?.series || {};
        const outKeys = getOutputKeys(indicator, indicatorMeta);

        if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
            try {
                const sample = Array.isArray(indicator.data) ? indicator.data.slice(0, 3) : indicator.data;
                console.log('[DMI][apply] outKeys:', outKeys, 'sample:', sample);
            } catch { /* ignore */ }
        }

        // Multi-output provided as array-of-objects with multiple keys
        if (Array.isArray(indicator.data) && outKeys.length > 1) {
            outKeys.forEach((key) => {
                const seriesId = `${indicator.id}-${key}`;
                const normalized = normalizeSeriesPoints(indicator.data, key);
                const padded = padIndicatorData(historicalData, normalized);

                const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                const vForKey = picked.visual;
                const fallbacks = {
                    color: indicator.params?.[`${key}_color`] || indicator.params?.color || '#FFD700',
                    lineWidth: indicator.params?.[`${key}_width`] || indicator.params?.width || 2,
                    lineStyle: indicator.params?.[`${key}_style`] || indicator.params?.style || LineStyle.Solid || 0,
                };
                const options = buildOptionsFromVisuals(vForKey, fallbacks);
                const { SeriesCtor, type } = resolveSeriesType(vForKey?.plotType || indicator.params?.[`${key}_plotType`] || indicator.params?.plotType || 'line');
                const typeOpts = toSeriesTypeOptions(type, options);
                const styledData = applySeriesPointVisuals(padded, type, options);

                const entry = currentSeries.get(seriesId);
                if (!entry) return;
                if (entry.plotType !== type) {
                    const pane = entry.pane?.paneIndex?.() ?? (isPaneIndicator ? 1 : 0);
                    chartRef.current.removeSeries(entry.series);
                    const newSeries = chartRef.current.addSeries(SeriesCtor, {
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                        title: `${indicator.name} ${key}`,
                    }, pane);
                    currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    setIndicatorSeriesData(newSeries, styledData, `${indicator.name}:${key}`, type);
                } else {
                    entry.series.applyOptions({
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                    });
                    setIndicatorSeriesData(entry.series, styledData, `${indicator.name}:${key}`, type);
                }
                const currentEntry = currentSeries.get(seriesId);
                if (currentEntry) currentEntry.pointVisualOptions = options;
            });
            return;
        }

        // Single value
        if (Array.isArray(indicator.data) && indicator.data.length > 0) {
            const valueKey = outKeys[0] || (indicator.data_keys && indicator.data_keys.length > 0 ? indicator.data_keys[0] : 'value');
            const normalized = normalizeSeriesPoints(indicator.data, valueKey);
            const padded = padIndicatorData(historicalData, normalized);

            const picked = pickSeriesVisual(visualsByKey, indicatorMeta, valueKey);
            const vForKey = picked.visual;
            const fallbacks = {
                color: indicator.params?.color || '#FFD700',
                lineWidth: indicator.params?.width || 2,
                lineStyle: indicator.params?.style || LineStyle.Solid || 0,
            };
            const options = buildOptionsFromVisuals(vForKey, fallbacks);
            const reqPlot = (vForKey?.plotType || indicator.params?.plotType || 'line');
            // If this is a pane indicator with a single line, switch to baseline to color negatives red
            const useBaseline = (indicator.paneType === 'pane') && (outKeys.length === 1) && (String(reqPlot).toLowerCase() === 'line');
            const chosenPlot = useBaseline ? 'baseline' : reqPlot;
            const { SeriesCtor, type } = resolveSeriesType(chosenPlot);
            const typeOpts = toSeriesTypeOptions(type, { ...options, negativeColor: NEGATIVE_LINE_COLOR });
            const styledData = applySeriesPointVisuals(padded, type, options);

            const entry = currentSeries.get(indicator.id);
            if (!entry) return; // nothing to update yet
            if (entry.plotType !== type) {
                const pane = entry.pane?.paneIndex?.() ?? (isPaneIndicator ? 1 : 0);
                chartRef.current.removeSeries(entry.series);
                const newSeries = chartRef.current.addSeries(SeriesCtor, {
                    ...typeOpts,
                    ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                    title: indicator.name,
                }, pane);
                currentSeries.set(indicator.id, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                setIndicatorSeriesData(newSeries, styledData, indicator.name, type);
            } else {
                entry.series.applyOptions({
                    ...typeOpts,
                    ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                });
                setIndicatorSeriesData(entry.series, styledData, indicator.name, type);
            }
            const currentEntry = currentSeries.get(indicator.id);
            if (currentEntry) currentEntry.pointVisualOptions = options;
            return;
        }

        // Multi-value object-of-arrays
        if (typeof indicator.data === 'object' && indicator.data) {
            Object.keys(indicator.data).forEach((key) => {
                const seriesId = `${indicator.id}-${key}`;
                const seriesData = indicator.data[key];
                if (!seriesData || seriesData.length === 0) return;

                const normalized = normalizeSeriesPoints(seriesData, key);
                if (isLoggingEnabled && indicator?.name?.toLowerCase() === 'dmi') {
                    try { console.log('[DMI][apply][norm]', key, normalized.slice(0, 3)); } catch { /* ignore */ }
                }
                const padded = padIndicatorData(historicalData, normalized);
                const picked = pickSeriesVisual(visualsByKey, indicatorMeta, key);
                const vForKey = picked.visual;
                const fallbacks = {
                    color: indicator.params?.[`${key}_color`] || '#FFD700',
                    lineWidth: indicator.params?.[`${key}_width`] || 2,
                    lineStyle: indicator.params?.[`${key}_style`] || LineStyle.Solid || 0,
                };
                const options = buildOptionsFromVisuals(vForKey, fallbacks);
                const reqPlot = (vForKey?.plotType || indicator.params?.[`${key}_plotType`] || 'line');
                // If this pane indicator only has one series key total, use baseline for negative coloring
                const keysAll = Object.keys(indicator.data || {});
                const useBaseline = (indicator.paneType === 'pane') && (keysAll.length === 1) && (String(reqPlot).toLowerCase() === 'line');
                const chosenPlot = useBaseline ? 'baseline' : reqPlot;
                const { SeriesCtor, type } = resolveSeriesType(chosenPlot);
                const typeOpts = toSeriesTypeOptions(type, { ...options, negativeColor: NEGATIVE_LINE_COLOR });
                const styledData = applySeriesPointVisuals(padded, type, options);

                const entry = currentSeries.get(seriesId);
                if (!entry) return;
                if (entry.plotType !== type) {
                    const pane = entry.pane?.paneIndex?.() ?? (isPaneIndicator ? 1 : 0);
                    chartRef.current.removeSeries(entry.series);
                    const newSeries = chartRef.current.addSeries(SeriesCtor, {
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                        title: `${indicator.name} ${key}`,
                    }, pane);
                    currentSeries.set(seriesId, { series: newSeries, pane: newSeries.getPane(), plotType: type });
                    setIndicatorSeriesData(newSeries, styledData, `${indicator.name}:${key}`, type);
                } else {
                    entry.series.applyOptions({
                        ...typeOpts,
                        ...(isPaneIndicator ? {} : { priceLineVisible: false }),
                    });
                    setIndicatorSeriesData(entry.series, styledData, `${indicator.name}:${key}`, type);
                }
                const currentEntry = currentSeries.get(seriesId);
                if (currentEntry) currentEntry.pointVisualOptions = options;
            });
        }
    };

    // Load overrides cache from localStorage once
    useEffect(() => {
        try {
            const raw = localStorage.getItem(OVERRIDES_STORAGE_KEY);
            if (raw) {
                const obj = JSON.parse(raw);
                const map = new Map(Object.entries(obj));
                visualOverridesCacheRef.current = map;
            }
    } catch (error) {
            console.error('Failed to load visual overrides cache', error);
        }
    }, []);

    const persistOverridesCache = useCallback(() => {
        try {
            const obj = Object.fromEntries(visualOverridesCacheRef.current);
            localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(obj));
        } catch (error) {
            console.error('Failed to persist visual overrides cache', error);
        }
    }, []);

    // Hydrate missing visual_overrides from cache when indicators (re)appear
    useEffect(() => {
        if (isBacktest) return;
        if (!Array.isArray(activeIndicators) || activeIndicators.length === 0) return;
        const cache = visualOverridesCacheRef.current;
        let cacheChanged = false;
        let stateChanged = false;
        const hydrated = activeIndicators.map((ind) => {
            if (!ind || Object.prototype.hasOwnProperty.call(ind, 'visual_overrides')) return ind;
            const sig = getIndicatorSignature(ind);
            const cached = cache.get(sig);
            if (!hasVisualOverrideValues(cached)) {
                if (cache.delete(sig)) cacheChanged = true;
                return ind;
            }
            stateChanged = true;
            return { ...ind, visual_overrides: cached };
        });
        if (cacheChanged) persistOverridesCache();
        if (stateChanged) setActiveIndicators(hydrated);
    }, [activeIndicators, isBacktest, persistOverridesCache, setActiveIndicators]);

    const handleUpdateIndicatorVisuals = (indicatorId, visualOverrides) => {
        if (!Array.isArray(activeIndicators)) return;
        const normalizedOverrides = visualOverrides && typeof visualOverrides === 'object'
            ? visualOverrides
            : {};
        const target = activeIndicators.find((indicator) => indicator.id === indicatorId);
        if (!target) return;
        const overridesChanged = stableStringify(target.visual_overrides || {})
            !== stableStringify(normalizedOverrides);
        const newActive = overridesChanged
            ? activeIndicators.map((indicator) => (
                indicator.id === indicatorId
                    ? { ...indicator, visual_overrides: normalizedOverrides }
                    : indicator
            ))
            : activeIndicators;
        if (overridesChanged) setActiveIndicators(newActive);

        // Update cache using the indicator signature
        const sig = getIndicatorSignature(target);
        if (hasVisualOverrideValues(normalizedOverrides)) {
            visualOverridesCacheRef.current.set(sig, normalizedOverrides);
        } else {
            visualOverridesCacheRef.current.delete(sig);
        }
        persistOverridesCache();

        // Also apply immediately to the existing series if present
        const updated = overridesChanged
            ? newActive.find((indicator) => indicator.id === indicatorId)
            : { ...target, visual_overrides: normalizedOverrides };
        // Give React a tick to commit state before applying options
        setTimeout(() => applyVisualsForIndicator(updated), 0);
    };

    // Update indicator parameters by removing and re-adding the indicator (live trading only)
    const handleUpdateIndicatorParams = (indicatorId, nextParams) => {
        if (isBacktest) return; // No param updates in backtest mode
        const target = Array.isArray(activeIndicators) ? activeIndicators.find(i => i.id === indicatorId) : null;
        if (!target) return;
        const name = target.name;
        const paneType = target.paneType;
        const scaleType = target.scaleType;
        const normalizedParams = nextParams && typeof nextParams === 'object' ? nextParams : {};
        const newKey = getIndicatorKey(name, normalizedParams, selectedSymbol, selectedTimeframe, paneType);

        if (newKey === target.id && stableStringify(normalizedParams) === stableStringify(target.params || {})) {
            return;
        }

        // Preserve any cached visual overrides by copying to the new signature as well
        try {
            const oldSig = getIndicatorSignature(target);
            const cached = visualOverridesCacheRef.current.get(oldSig);
            if (cached) {
                const fakeNext = { ...target, params: normalizedParams };
                const newSig = getIndicatorSignature(fakeNext);
                visualOverridesCacheRef.current.set(newSig, cached);
                persistOverridesCache();
            }
        } catch { /* ignore */ }

        // Replace the client-side definition immediately. Waiting for the
        // backend removal acknowledgement can leave both parameter variants
        // visible when the new result arrives first (or the acknowledgement is
        // lost). Runtime data belongs to the old parameter set and is discarded.
        const replacement = {
            ...target,
            id: newKey,
            backendUniqueId: null,
            params: normalizedParams,
            data: Array.isArray(target.data) ? [] : {},
            data_keys: [],
            events: [],
        };
        if (typeof removeActiveIndicator === 'function') {
            removeActiveIndicator(indicatorId);
        }
        setActiveIndicators(activeIndicators.map((indicator) => (
            indicator.id === indicatorId ? replacement : indicator
        )));

        // Backward-compatible fallback for feed implementations without the
        // atomic removal API.
        if (typeof removeActiveIndicator !== 'function' && target.backendUniqueId) {
            try { sendIndicatorAction('remove_indicator', { unique_id: target.backendUniqueId }); } catch { /* ignore */ }
        }

        // Add a new instance with updated params and a fresh unique id
        try {
            sendIndicatorAction('add_indicator', {
                indicator: name,
                params: normalizedParams,
                unique_id: newKey,
                count: 2700,
                pane_type: paneType,
                scale_type: scaleType,
            });
        } catch (e) {
            console.error('Failed to send update (remove+add) for indicator params:', e);
        }
    };

    // Overlay label actions
    const isOverlayVisible = (indicatorId) => {
        const target = Array.isArray(activeIndicators) ? activeIndicators.find(i => i.id === indicatorId) : null;
        if (!target) return true;
        const eff = getEffectiveVisuals(target, availableIndicators);
        const keys = Object.keys(eff.series || {})
            .concat(getOutputKeys(target, getIndicatorMeta(availableIndicators, target)))
            .filter((v, i, a) => v && a.indexOf(v) === i);
        if (keys.length === 0) return true;
        // Visible if any key not explicitly false
        return keys.some(k => eff.series?.[k]?.visible !== false);
    };

    const toggleOverlayVisibilityById = (indicatorId) => {
        const target = Array.isArray(activeIndicators) ? activeIndicators.find(i => i.id === indicatorId) : null;
        if (!target) return;
        const eff = getEffectiveVisuals(target, availableIndicators);
        const keys = Object.keys(eff.series || {})
            .concat(getOutputKeys(target, getIndicatorMeta(availableIndicators, target)))
            .filter((v, i, a) => v && a.indexOf(v) === i);
        const anyVisible = keys.length === 0 ? true : keys.some(k => eff.series?.[k]?.visible !== false);
        const nextVisible = !anyVisible;
        const draft = { series: {} };
        (keys.length ? keys : ['default']).forEach((k) => {
            draft.series[k] = { ...(eff.series?.[k] || {}), visible: nextVisible };
        });
        handleUpdateIndicatorVisuals(indicatorId, buildOverridesAgainstDefaults(target, draft));
    };

    // Build overrides against defaults for a single indicator
    const buildOverridesAgainstDefaults = (indicator, draft) => {
        const meta = getIndicatorMeta(availableIndicators, indicator);
        const defaults = meta?.visual_defaults || {};
        const overrides = { series: {} };
        const draftSeries = draft.series || {};
        const defaultSeries = defaults.series || {};
        Object.keys(draftSeries).forEach((k) => {
            const d = draftSeries[k] || {};
            const def = defaultSeries[k] || {};
            const diff = {};
            Object.keys(d).forEach((f) => {
                if (d[f] !== def[f] && d[f] !== undefined) diff[f] = d[f];
            });
            if (Object.keys(diff).length) overrides.series[k] = diff;
        });
        return overrides;
    };

    // Open the same visual editor used in IndicatorControls via a custom event
    const openOverlayVisualEditor = (indicatorId) => {
        try {
            const evt = new CustomEvent('open-indicator-visual-editor', { detail: { indicatorId } });
            window.dispatchEvent(evt);
    } catch { /* ignore */ }
    };

    // Clear all indicator series on instrument/context change to avoid duplicates
    useEffect(() => {
        const resetContext = `${chartDataContextRef.current}|${isBacktest ? 'backtest' : 'live'}`;
        if (chartResetContextRef.current === resetContext) return;
        chartResetContextRef.current = resetContext;

        if (isBacktest) return;
        clearMarkerPrimitives();
        setChartRenderError(null);
        const chart = chartRef.current;
        const currentSeries = indicatorSeriesRef.current;
        if (chart && currentSeries.size > 0) {
            currentSeries.forEach(({ series }) => {
                try { chart.removeSeries(series); } catch { /* ignore */ }
            });
            currentSeries.clear();
        }
        // Indicator definitions are retained by PriceFeedProvider and rehydrated
        // with data for the new account/symbol/timeframe context.
        // Also immediately clear the main price series so no old bars remain visible
        if (seriesRef.current) {
            try { seriesRef.current.setData([]); } catch { /* ignore */ }
        }
    // Remove volume pane
    removeVolumeSeries();
        barsRef.current = [];
        timeToIndexRef.current = new Map();
        mainSeriesStateRef.current = {
            contextKey: chartDataContextRef.current,
            lastTime: null,
        };
        pendingScrollRestoreRef.current = null;
    }, [selectedSymbol, selectedTimeframe, selectedAccount, isBacktest, clearMarkerPrimitives, removeVolumeSeries]);

    // Clear spec overlay series on instrument/context change
    useEffect(() => {
        if (isBacktest) return;
        const chart = chartRef.current;
        if (chart && specSeriesRef.current.size > 0) {
            specSeriesRef.current.forEach((series) => {
                try { chart.removeSeries(series); } catch { /* ignore */ }
            });
            specSeriesRef.current.clear();
        }
    }, [selectedSymbol, selectedTimeframe, selectedAccount, isBacktest]);

    // Deterministic color per nested spec series key
    const colorForKey = useCallback((key) => {
        const s = String(key || '');
        let h = 0;
        for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
        const idx = Math.abs(h) % indicatorColors.length;
        return indicatorColors[idx] || '#FFD700';
    }, []);

    // Ensure a nested spec overlay series exists
    const ensureSpecSeries = useCallback((seriesKey, hostPane) => {
        const chart = chartRef.current;
        if (!chart) return null;
        const existing = specSeriesRef.current.get(seriesKey);
        if (existing) return existing;
        try {
            // Prefer adding to the same pane as the host indicator if provided
            const options = { color: colorForKey(seriesKey), lineWidth: 2 };
            // If rendering on the main pane (overlay), hide dotted current-value line
            if (hostPane === 0 || hostPane === undefined || hostPane === null) {
                options.priceLineVisible = false;
            }
            const s = chart.addSeries(LineSeries, options, (hostPane !== undefined && hostPane !== null) ? hostPane : undefined);
            specSeriesRef.current.set(seriesKey, s);
            return s;
        } catch {
            return null;
        }
    }, [colorForKey]);

    // Drive spec overlay series from specStreams (seed and updates are reflected via state changes)
    useEffect(() => {
        if (isBacktest) return;
        const chart = chartRef.current;
        if (!chart || !specStreams || typeof specStreams !== 'object') return;
        // Helper: sort ascending by time and remove duplicate/equal times (keep last)
        const sortAscUnique = (pts) => {
            if (!Array.isArray(pts)) return [];
            const a = pts.slice().sort((x, y) => (x.time - y.time));
            const out = [];
            let lastTime = -Infinity;
            for (const pt of a) {
                if (typeof pt?.time !== 'number' || !Number.isFinite(pt.time)) continue;
                if (pt.time > lastTime) {
                    out.push(pt);
                    lastTime = pt.time;
                } else if (pt.time === lastTime && out.length > 0) {
                    // replace previous at same time with the latest
                    out[out.length - 1] = pt;
                }
            }
            return out;
        };
        const expected = new Set();
        Object.entries(specStreams).forEach(([uid, entry]) => {
            if (!entry || entry.data == null) return;
            // If this entry lacks both attachToId and attachToMeta, treat as orphan and skip
            if (!entry.attachToId && !entry.attachToMeta) {
                if (isLoggingEnabled) console.warn('[SpecOverlay] skip orphan spec stream', { uid });
                return;
            }
            // If this stream is attached to a specific indicator pane, try to reuse that pane (by index)
            let targetPaneIndex = undefined;
            const resolveHostByMeta = () => {
                const meta = entry.attachToMeta;
                if (!meta) return undefined;
                // Try id first
                let host = undefined;
                if (meta.parentId) {
                    host = indicatorSeriesRef.current.get(meta.parentId);
                    if (!host) {
                        for (const [k, v] of indicatorSeriesRef.current.entries()) {
                            if (typeof k === 'string' && k.startsWith(meta.parentId + '-')) { host = v; break; }
                        }
                    }
                }
                // Fallback by matching current activeIndicators by name/params
                if (!host && meta.parentName) {
                    const candidates = activeIndicators.filter(i => String(i.name).toLowerCase() === String(meta.parentName).toLowerCase());
                    let chosen = candidates[0];
                    if (meta.parentParams && candidates.length > 1) {
                        const want = JSON.stringify(meta.parentParams);
                        chosen = candidates.find(c => JSON.stringify(c.params||{}) === want) || candidates[0];
                    }
                    if (chosen) {
                        // Use indicatorSeriesRef to get a concrete series entry for this indicator id
                        host = indicatorSeriesRef.current.get(chosen.id);
                        if (!host) {
                            for (const [k, v] of indicatorSeriesRef.current.entries()) {
                                if (typeof k === 'string' && k.startsWith(chosen.id + '-')) { host = v; break; }
                            }
                        }
                    }
                }
                if (isLoggingEnabled) console.log('[SpecOverlay] resolveHostByMeta', { meta, found: !!host });
                return host;
            };
            if (entry.attachToId || entry.attachToMeta) {
                let host = indicatorSeriesRef.current.get(entry.attachToId);
                if (!host) {
                    // Fallback: multi-output indicators are stored as `${id}-${key}`; pick any
                    for (const [k, v] of indicatorSeriesRef.current.entries()) {
                        if (typeof k === 'string' && k.startsWith(entry.attachToId + '-')) { host = v; break; }
                    }
                }
                if (!host) host = resolveHostByMeta();
                if (host && host.pane && typeof host.pane.paneIndex === 'function') {
                    const idx = host.pane.paneIndex();
                    if (typeof idx === 'number') targetPaneIndex = idx;
                }
                if (isLoggingEnabled) console.log('[SpecOverlay] host pane', { attachToId: entry.attachToId, attachToMeta: entry.attachToMeta, targetPaneIndex });
            }
            const keys = Array.isArray(entry.data_keys) && entry.data_keys.length > 0 ? entry.data_keys : ['value'];
            const valueForPoint = (p, k) => {
                if (!p) return undefined;
                if (Number.isFinite(p.value)) return p.value;
                if (k && Number.isFinite(p[k])) return p[k];
                if (p.value && typeof p.value === 'object' && Number.isFinite(p.value[k])) return p.value[k];
                if (p.outputs && typeof p.outputs === 'object' && Number.isFinite(p.outputs[k])) return p.outputs[k];
                // Fallback: first numeric field other than time
                for (const kk of Object.keys(p)) {
                    if (kk === 'time') continue;
                    const v = p[kk];
                    if (Number.isFinite(v)) return v;
                }
                return undefined;
            };
            if (Array.isArray(entry.data)) {
                const key = `${uid}:value`;
                expected.add(key);
                // If attached to a parent but no pane yet, defer until pane resolves to avoid landing on wrong pane
                if ((entry.attachToId || entry.attachToMeta) && typeof targetPaneIndex !== 'number') {
                    if (isLoggingEnabled) console.log('[SpecOverlay] defer create (no pane yet)', { key, attachToId: entry.attachToId, attachToMeta: entry.attachToMeta });
                    return;
                }
                let s = ensureSpecSeries(key, targetPaneIndex);
                // If we created without targetPane earlier, move to target pane when it appears by recreating
                if (s && typeof targetPaneIndex === 'number') {
                    try {
                        const currentPane = s.getPane && s.getPane();
                        const currentIdx = currentPane && currentPane.paneIndex ? currentPane.paneIndex() : undefined;
                        if (typeof currentIdx === 'number' && currentIdx !== targetPaneIndex) {
                            if (isLoggingEnabled) console.log('[SpecOverlay] relocating series', { key, from: currentIdx, to: targetPaneIndex });
                            chart.removeSeries(s);
                            specSeriesRef.current.delete(key);
                            s = ensureSpecSeries(key, targetPaneIndex);
                        }
                    } catch { /* ignore */ }
                }
                if (s) {
                    const primary = keys[0];
                    const filtered = entry.data
                        .map(p => ({ p, v: valueForPoint(p, primary) }))
                        .filter(({ p, v }) => typeof p?.time === 'number' && Number.isFinite(p.time) && Number.isFinite(v));
                    if (isLoggingEnabled && filtered.length === 0 && Array.isArray(entry.data) && entry.data.length > 0) {
                        const sample = entry.data[entry.data.length - 1];
                        console.warn('[SpecOverlay] No numeric points found for single-series', { key, primary, sampleKeys: Object.keys(sample || {}) });
                    }
                    if (isLoggingEnabled) console.log('[SpecOverlay] setData', { key, points: filtered.length, attachToId: entry.attachToId, primary });
                    const points = filtered.map(({ p, v }) => ({ time: p.time > 1000000000000 ? Math.floor(p.time/1000) : p.time, value: v }));
                    s.setData(sortAscUnique(points));
                }
            } else if (typeof entry.data === 'object') {
                Object.keys(entry.data).forEach((k) => {
                    const key = `${uid}:${k}`;
                    expected.add(key);
                    if ((entry.attachToId || entry.attachToMeta) && typeof targetPaneIndex !== 'number') {
                        if (isLoggingEnabled) console.log('[SpecOverlay] defer create (no pane yet)[key]', { key, attachToId: entry.attachToId, attachToMeta: entry.attachToMeta });
                        return;
                    }
                    let s = ensureSpecSeries(key, targetPaneIndex);
                    if (s && typeof targetPaneIndex === 'number') {
                        try {
                            const currentPane = s.getPane && s.getPane();
                            const currentIdx = currentPane && currentPane.paneIndex ? currentPane.paneIndex() : undefined;
                            if (typeof currentIdx === 'number' && currentIdx !== targetPaneIndex) {
                                if (isLoggingEnabled) console.log('[SpecOverlay] relocating series[key]', { key, from: currentIdx, to: targetPaneIndex });
                                chart.removeSeries(s);
                                specSeriesRef.current.delete(key);
                                s = ensureSpecSeries(key, targetPaneIndex);
                            }
                        } catch { /* ignore */ }
                    }
                    if (s) {
                        const filtered = (entry.data[k] || [])
                            .map(p => ({ p, v: valueForPoint(p, k) }))
                            .filter(({ p, v }) => typeof p?.time === 'number' && Number.isFinite(p.time) && Number.isFinite(v));
                        if (isLoggingEnabled && filtered.length === 0 && Array.isArray(entry.data[k]) && entry.data[k].length > 0) {
                            const sample = entry.data[k][entry.data[k].length - 1];
                            console.warn('[SpecOverlay] No numeric points found for multi-series', { key, seriesKey: k, sampleKeys: Object.keys(sample || {}) });
                        }
                        if (isLoggingEnabled) console.log('[SpecOverlay] setData[key]', { key, points: filtered.length, attachToId: entry.attachToId });
                        const points = filtered.map(({ p, v }) => ({ time: p.time > 1000000000000 ? Math.floor(p.time/1000) : p.time, value: v }));
                        s.setData(sortAscUnique(points));
                    }
                });
            }
        });
        // Clean up series that are no longer present
        specSeriesRef.current.forEach((series, key) => {
            if (!expected.has(key)) {
                try { chart.removeSeries(series); } catch { /* ignore */ }
                specSeriesRef.current.delete(key);
            }
        });
    }, [specStreams, activeIndicators, isBacktest, ensureSpecSeries, isLoggingEnabled]);

    // Auto-unsubscribe orphan overlays when parent indicator is removed
    const missingParentSinceRef = useRef(new Map()); // uid -> timestamp
    useEffect(() => {
        if (isBacktest) return;
        if (!specStreams || typeof specStreams !== 'object') return;
        const indicatorIds = new Set(activeIndicators.map(i => i.id));
        Object.entries(specStreams).forEach(([uid, entry]) => {
            if (!entry) return;
            const parentId = entry.attachToId || entry.attachToMeta?.parentId;
            const meta = entry.attachToMeta;
            const parentPresentByMeta = meta ? activeIndicators.some(i => {
                if (String(i.name).toLowerCase() !== String(meta.parentName).toLowerCase()) return false;
                const a = JSON.stringify(i.params||{});
                const b = JSON.stringify(meta.parentParams||{});
                return a === b;
            }) : false;
            if (!parentId && !parentPresentByMeta) return;
            if (!indicatorIds.has(parentId) && !parentPresentByMeta) {
                try {
                    // Grace period: parent can be temporarily missing during TF/symbol/account switches
                    const now = Date.now();
                    const first = missingParentSinceRef.current.get(uid) || now;
                    missingParentSinceRef.current.set(uid, first);
                    const GRACE_MS = 2000;
                    if (now - first < GRACE_MS) return; // wait and see if parent comes back

                    // remove any existing overlay series for this uid
                    const chart = chartRef.current;
                    if (chart) {
                        // remove both single and multi keys
                        const keys = [];
                        if (Array.isArray(entry.data)) keys.push(`${uid}:value`);
                        else if (entry.data && typeof entry.data === 'object') {
                            Object.keys(entry.data).forEach(k => keys.push(`${uid}:${k}`));
                        }
                        keys.forEach(k => {
                            const s = specSeriesRef.current.get(k);
                            if (s) {
                                const pane = s.getPane && s.getPane();
                                try { chart.removeSeries(s); } catch { /* ignore */ }
                                specSeriesRef.current.delete(k);
                                // if pane is empty and not main, remove it
                                try {
                                    if (pane && typeof pane.getSeries === 'function' && pane.paneIndex && pane.paneIndex() > 0) {
                                        if ((pane.getSeries()?.length || 0) === 0) {
                                            chart.removePane(pane.paneIndex());
                                        }
                                    }
                                } catch { /* ignore */ }
                            }
                        });
                    }
                    unsubscribeSpec && unsubscribeSpec(uid);
                    missingParentSinceRef.current.delete(uid);
                } catch { /* ignore */ }
            } else {
                // parent present; clear any grace timers
                if (missingParentSinceRef.current.has(uid)) missingParentSinceRef.current.delete(uid);
            }
        });
    }, [activeIndicators, specStreams, isBacktest, unsubscribeSpec]);

    // Timeframe overlay options (fallback if not provided)
    const defaultTimeframes = [
        { label: '1M', value: 'M1' },
        { label: '5M', value: 'M5' },
        { label: '15M', value: 'M15' },
        { label: '30M', value: 'M30' },
        { label: '1H', value: 'H1' },
        { label: '4H', value: 'H4' },
        { label: '1D', value: 'D1' },
        { label: '1W', value: 'W1' },
        { label: '1MN', value: 'MN1' },
    ];
    const tfOptions = timeframeOptions && Array.isArray(timeframeOptions) ? timeframeOptions : defaultTimeframes;

    return (
        <div className="w-full h-full min-h-[300px] flex flex-col">
            {(!isBacktest || showControls) && (
                <IndicatorControls
                    onAddIndicator={handleAddIndicator}
                    onRemoveIndicator={handleRemoveIndicator}
                    onUpdateIndicatorParams={handleUpdateIndicatorParams}
                    activeIndicators={activeIndicators}
                    availableIndicators={availableIndicators}
                    chartProfiles={chartProfiles}
                    selectedProfile={selectedProfile}
                    onLoadProfile={handleLoadProfile}
                    onSaveProfile={handleSaveProfile}
                    onDeleteProfile={handleDeleteProfile}
                    onUpdateIndicatorVisuals={handleUpdateIndicatorVisuals}
                    compact={controlsCompact}
                    showRecentTrades={showRecentTrades}
                    onToggleRecentTrades={onToggleRecentTrades}
                    showVolume={showVolume}
                    onToggleVolume={() => setShowVolume(v => !v)}
                    timeframeOptions={tfOptions}
                    selectedTimeframe={selectedTimeframe}
                    onTimeframeChange={onTimeframeChange}
                    activeDrawingTool={activeDrawingTool}
                    onSelectDrawingTool={handleSelectDrawingTool}
                    onDeleteSelectedDrawing={handleDeleteSelectedDrawing}
                    onDeleteAllDrawings={handleDeleteAllDrawings}
                    onUpdateDrawing={handleUpdateDrawing}
                    onSelectDrawing={handleSelectDrawing}
                    drawingSelection={drawingSelection}
                    selectedDrawingId={selectedDrawingId}
                    drawingCount={drawingCount}
                    drawings={drawings}
                    chartAnalysisEnabled={enableStudioChartAnalysis && !isBacktest}
                    onAnalyzeChart={handleLaunchChartAnalysis}
                    chartAnalysisBusy={chartAnalysisState.status === 'launching' || chartAnalysisState.status === 'running'}
                />
            )}
            <div ref={paneOverlayHostRef} className="relative w-full flex-1 min-h-0 px-2 pb-2 pt-1 sm:px-3 sm:pb-3 sm:pt-2">
                {isBacktest && !showControls && (
                    <div className="absolute top-2 left-2 z-30 flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-7 px-2 py-0 text-xs" onClick={() => setShowVolume(v => !v)} title="Toggle Volume Bars">
                            {showVolume ? 'Hide Vol' : 'Show Vol'}
                        </Button>
                    </div>
                )}
                {isBacktest && showVolume && backtestVolumeOverlayLabels.length > 0 && (
                    <div
                        className="absolute z-30 flex flex-col gap-1 left-2"
                        style={{
                            top: `calc(var(--chart-pane-top-${volumePaneIndexRef.current ?? 0}, 0px) + 4px)`,
                            visibility: `var(--chart-pane-visibility-${volumePaneIndexRef.current ?? 0}, hidden)`,
                        }}
                    >
                        {backtestVolumeOverlayLabels.map(l => (
                            <div key={l.id} className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-slate-900/70 backdrop-blur text-[10px] leading-tight text-slate-200 border border-slate-700 shadow-sm">
                                <span className="inline-block w-2 h-2 rounded" style={{ background: l.color }} />
                                <span className="select-none">{l.text}</span>
                            </div>
                        ))}
                    </div>
                )}
                {/* Removed demo EMA(RSI) buttons */}
                {showTimeframeOverlay && !isBacktest && !onTimeframeChange && (
                    <div className="absolute top-2 left-2 z-20 flex items-center gap-2">
                        <CustomSelect
                            options={tfOptions}
                            value={selectedTimeframe}
                            onChange={(val) => onTimeframeChange && onTimeframeChange(val)}
                            placeholder="TF"
                            className="w-36"
                            dropdownFitContent={true}
                            optionPaddingLeft={16}
                        />
                    </div>
                )}
                {/* Pane indicator chips (inside panes, top-left). Main price pane (0) excluded. */}
                {paneLabels.filter(l => l.paneIndex !== 0).map(l => {
                    const fallbackTop = 6 + (l.paneIndex * 120);
                    return (
                        <div key={`${l.paneIndex}-${l.id}`}
                             className="absolute left-3 z-30"
                             style={{
                                 top: `calc(var(--chart-pane-top-${l.paneIndex}, ${fallbackTop}px) + 2px)`,
                                 visibility: `var(--chart-pane-visibility-${l.paneIndex}, hidden)`,
                             }}>
                            <div className="flex items-center gap-1.5 pl-2 pr-1.5 py-0.5 rounded bg-slate-800/70 backdrop-blur text-[11px] leading-tight text-slate-200/90 shadow-sm border border-slate-700/60">
                                <span className="mr-1 select-none">{l.text}</span>
                                <div className="flex items-center gap-0.5 ml-1">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-textSecondary hover:text-destructive" onClick={() => toggleOverlayVisibilityById(l.id)} title="Hide/Show">
                                        {isOverlayVisible(l.id) ? <IconEye/> : <IconEyeOff/>}
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-secondary hover:text-textPrimary" onClick={() => openOverlayVisualEditor(l.id)} title="Edit visuals">
                                        <IconSliders/>
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-destructive hover:text-destructiveHover" onClick={() => handleRemoveIndicator(l.id)} title="Remove indicator">
                                        <IconTrash/>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {/* Overlay indicator labels (main pane, top-left) with actions */}
                {!isBacktest && overlayLabels.length > 0 && (
                    <div className="absolute top-3 left-3 z-30 flex flex-col gap-1">
                        {overlayLabels.map((l) => (
                <div key={l.id} className="flex items-center gap-1.5 pl-2 pr-1.5 py-0.5 rounded bg-slate-800/70 backdrop-blur text-[11px] leading-tight text-slate-200/90 shadow-sm border border-slate-700/60">
                                <span className="mr-1 select-none">{l.text}</span>
                                <div className="flex items-center gap-0.5 ml-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-textSecondary hover:text-destructive" onClick={() => toggleOverlayVisibilityById(l.id)} title="Hide/Show">
                                        {isOverlayVisible(l.id) ? <IconEye/> : <IconEyeOff/>}
                                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-secondary hover:text-textPrimary" onClick={() => openOverlayVisualEditor(l.id)} title="Edit visuals">
                                        <IconSliders/>
                                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-1 text-destructive hover:text-destructiveHover" onClick={() => handleRemoveIndicator(l.id)} title="Remove indicator">
                                        <IconTrash/>
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="relative w-full h-full">
                    <div
                        ref={chartContainerRef}
                        className="w-full h-full"
                    />
                    {!isBacktest && historyStatus === 'loading' && !chartRenderError && (
                        <div data-testid="chart-history-loading" className="absolute inset-0 z-30 grid place-items-center bg-slate-950/35 text-sm text-slate-300">
                            Loading chart history...
                        </div>
                    )}
                    {!isBacktest && (chartRenderError || ['empty', 'error'].includes(historyStatus)) && (
                        <div role="alert" data-testid="chart-history-state" className="absolute inset-0 z-30 grid place-items-center bg-slate-950/80 p-6 text-center">
                            <div className="max-w-sm space-y-3">
                                <p className="text-sm text-slate-200">
                                    {chartRenderError || historyError || 'No renderable chart history is available for this selection.'}
                                </p>
                                <Button size="sm" variant="outline" onClick={() => retryHistory?.()}>
                                    Retry
                                </Button>
                            </div>
                        </div>
                    )}
                    {/* Drawing tools canvas overlay – sits on top of the chart */}
                    <canvas
                        ref={drawingCanvasRef}
                        className="absolute inset-0 w-full h-full"
                        style={{ pointerEvents: 'none', zIndex: 25 }}
                    />
                </div>

            </div>
            <Modal
                open={chartAnalysisModalOpen}
                onClose={() => {
                    if (analysisPollTimeoutRef.current) {
                        clearTimeout(analysisPollTimeoutRef.current);
                        analysisPollTimeoutRef.current = null;
                    }
                    setChartAnalysisModalOpen(false);
                }}
                title={`Chart analysis: ${selectedSymbol || 'Chart'} ${selectedTimeframe || ''}`.trim()}
                maxWidthClass="max-w-4xl"
                footer={(
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if (analysisPollTimeoutRef.current) {
                                    clearTimeout(analysisPollTimeoutRef.current);
                                    analysisPollTimeoutRef.current = null;
                                }
                                setChartAnalysisModalOpen(false);
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleLaunchChartAnalysis}
                            disabled={chartAnalysisState.status === 'launching' || chartAnalysisState.status === 'running'}
                        >
                            {chartAnalysisState.status === 'launching' || chartAnalysisState.status === 'running' ? 'Running…' : 'Run again'}
                        </Button>
                    </>
                )}
            >
                <div className="space-y-4 text-sm">
                    {chartAnalysisState.preflight && chartAnalysisState.preflight.show_estimates_enabled !== false && (
                        <div className="rounded-lg border border-borderDefault bg-background/40 p-3">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-textSecondary">Preflight</div>
                            <div className="grid gap-2 sm:grid-cols-3">
                                <div>
                                    <div className="text-xs text-textSecondary">Tariff</div>
                                    <div className="font-medium">{chartAnalysisState.preflight.tariff_class || 'chart_analysis_standard'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-textSecondary">Estimated credits</div>
                                    <div className="font-medium">{chartAnalysisState.preflight.estimated_credits ?? '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-textSecondary">Reserve required</div>
                                    <div className="font-medium">{chartAnalysisState.preflight.reserve_required ? 'Yes' : 'No'}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {(chartAnalysisState.status === 'launching' || chartAnalysisState.status === 'running') && (
                        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-textPrimary">
                            <div className="font-medium">Chart analysis is running.</div>
                            <div className="mt-1 text-xs text-textSecondary">
                                The current chart context was submitted to the bounded chart-analysis workflow.
                            </div>
                        </div>
                    )}

                    {chartAnalysisState.error && (
                        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-destructive">
                            {chartAnalysisState.error}
                        </div>
                    )}

                    {chartAnalysisState.result && (
                        <div className="rounded-lg border border-borderDefault bg-background/30 p-4">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-textSecondary">Analysis</div>
                            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-6 text-textPrimary">
                                {extractAnalysisText(chartAnalysisState.result)}
                            </pre>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
     );
 };

 export default TradingChart;

// Icons (copied to match IndicatorControls)
const IconEye = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
);
const IconEyeOff = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.94"/>
        <path d="M1 1l22 22"/>
    </svg>
);
const IconSliders = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3"/>
        <circle cx="4" cy="14" r="2"/>
        <circle cx="12" cy="12" r="2"/>
        <circle cx="20" cy="16" r="2"/>
    </svg>
);
const IconTrash = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
);

// Detect multi-output indicators represented as array-of-objects with multiple data_keys
// function isMultiOutputArrayIndicator(indicator) {
//     return Array.isArray(indicator?.data) && Array.isArray(indicator?.data_keys) && indicator.data_keys.length > 1;
// }

// Build a short label like "EMA 21" or "MACD 12/26/9" when disambiguation is needed
function buildOverlayLabel(name, params, includeParams) {
    const base = String(name || '').trim();
    if (!includeParams) return base;
    const p = params || {};
    const low = base.toLowerCase();
    // Common patterns
    if (low.includes('ema') || low === 'ema') {
        const len = p.length ?? p.period ?? p.n ?? p.window ?? p.len;
        return len != null ? `${base} ${len}` : base;
    }
    if (low.includes('sma') || low === 'sma') {
        const len = p.length ?? p.period ?? p.n ?? p.window ?? p.len;
        return len != null ? `${base} ${len}` : base;
    }
    if (low === 'rsi') {
        const len = p.length ?? p.period ?? p.window ?? p.len ?? p.n;
        return len != null ? `${base} ${len}` : base;
    }
    if (low === 'macd') {
        const fast = p.fast ?? p.fastLength ?? p.fast_period;
        const slow = p.slow ?? p.slowLength ?? p.slow_period;
        const sig = p.signal ?? p.signalLength ?? p.signal_period;
        const trio = [fast, slow, sig].filter(v => v != null).join('/');
        return trio ? `${base} ${trio}` : base;
    }
    if (low.includes('boll') || low === 'bb' || low === 'bollinger bands') {
        const len = p.length ?? p.period ?? p.n ?? p.window ?? p.len;
        const dev = p.std ?? p.deviation ?? p.deviations ?? p.stdDev ?? p.k;
        const duo = [len, dev].filter(v => v != null).join('/');
        return duo ? `${base} ${duo}` : base;
    }
    // Generic fallback: show first 1-3 numeric params
    const numericEntries = Object.entries(p).filter(([, v]) => typeof v === 'number');
    if (numericEntries.length === 0) return base;
    const short = numericEntries.slice(0, 3).map(([, v]) => v).join('/');
    return short ? `${base} ${short}` : base;
}

// Normalize an array of raw points to [{time, value}] selecting a numeric value by priority
function keyVariants(k) {
    if (!k || typeof k !== 'string') return [];
    const snake = k;
    const parts = k.split(/[_-]/g);
    const camel = parts[0] + parts.slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
    const pascal = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
    const flat = parts.join('');
    return [snake, camel, pascal, flat];
}

function getFirstNumericByKeyVariants(obj, keyHint) {
    if (!obj || typeof obj !== 'object' || !keyHint) return undefined;
    for (const k of keyVariants(keyHint)) {
        const v = obj[k];
        if (Number.isFinite(v)) return v;
    }
    return undefined;
}

function normalizeSeriesPoints(arr, keyHint) {
    if (!Array.isArray(arr)) return [];
    const normalized = arr
        .filter(d => d && typeof d.time === 'number')
        .map(d => {
            let v = undefined;
            // Prefer key-specific values first
            v = getFirstNumericByKeyVariants(d, keyHint);
            if (v === undefined && d.data && typeof d.data === 'object') v = getFirstNumericByKeyVariants(d.data, keyHint);
            if (v === undefined && d.value && typeof d.value === 'object') v = getFirstNumericByKeyVariants(d.value, keyHint);
            if (v === undefined && d.outputs && typeof d.outputs === 'object') v = getFirstNumericByKeyVariants(d.outputs, keyHint);
            // Then generic forms
            if (v === undefined && Number.isFinite(d.value)) v = d.value;
            if (v === undefined && Number.isFinite(d.v)) v = d.v;
            if (v === undefined && d.data && typeof d.data === 'object') {
                for (const k of Object.keys(d.data)) {
                    const val = d.data[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            if (v === undefined && d.value && typeof d.value === 'object') {
                for (const k of Object.keys(d.value)) {
                    const val = d.value[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            if (v === undefined && d.outputs && typeof d.outputs === 'object') {
                for (const k of Object.keys(d.outputs)) {
                    const val = d.outputs[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            if (v === undefined) {
                for (const k of Object.keys(d)) {
                    if (k === 'time') continue;
                    const val = d[k];
                    if (Number.isFinite(val)) { v = val; break; }
                }
            }
            let t = d.time;
            if (t > 1000000000000) t = Math.floor(t / 1000);
            return v === undefined ? { time: t } : { time: t, value: v };
        });
    return canonicalizeIndicatorData(normalized);
}

function detectOutputKeysFromData(indicator) {
    if (!Array.isArray(indicator?.data) || indicator.data.length === 0) return [];
    const sample = indicator.data.find(x => x && typeof x === 'object');
    if (!sample) return [];
    if (sample.outputs && typeof sample.outputs === 'object') return Object.keys(sample.outputs);
    if (sample.value && typeof sample.value === 'object') return Object.keys(sample.value);
    if (sample.data && typeof sample.data === 'object') return Object.keys(sample.data);
    // If flat keys present (excluding time/value)
    const flat = Object.keys(sample).filter(k => k !== 'time' && k !== 'value' && k !== 'outputs' && k !== 'data');
    // Only return if there are multiple numeric-looking keys
    if (flat.length > 1) return flat;
    return [];
}

// Create masked versions of points keeping only values with desired sign; others become whitespace bars
// Note: previously used dual-series masking for slope outputs was removed per design change.

function getOutputKeys(indicator, indicatorMeta) {
    if (Array.isArray(indicator?.data_keys) && indicator.data_keys.length > 0) return indicator.data_keys;
    const fromData = detectOutputKeysFromData(indicator);
    if (fromData.length > 0) return fromData;
    if (Array.isArray(indicatorMeta?.outputs) && indicatorMeta.outputs.length > 0) return indicatorMeta.outputs;
    return [];
}

// ---- Overlay label actions helpers ----
// function getSeriesKeysForIndicator(indicator, availableIndicators) {
//     const meta = getIndicatorMeta(availableIndicators, indicator?.name);
//     const schemaKeys = meta?.visual_schema?.series ? Object.keys(meta.visual_schema.series) : [];
//     const defaultKeys = meta?.visual_defaults?.series ? Object.keys(meta.visual_defaults.series) : [];
//     const keys = schemaKeys.length ? schemaKeys : (defaultKeys.length ? defaultKeys : ['default']);
//     return keys;
// }

function getEffectiveVisuals(indicator, availableIndicators) {
    const meta = getIndicatorMeta(availableIndicators, indicator);
    const defaults = meta?.visual_defaults || {};
    const overrides = indicator?.visual_overrides || {};
    return deepMerge(defaults, overrides);
}
